/* ==========================================================================
   COMNITO APP — v2.0
   One bundle for every Comnito page on Shopify.

   Upload as a theme asset:  assets/comnito-app.js
   Include on a page with a script tag pointing at:
                             {{ 'comnito-app.js' | asset_url }}   (use defer)

   Every page identifies itself with two attributes on <body> (or on the
   wrapper div inside a Shopify page):

     data-page    -> which bottom-nav tab lights up  (home|nearby|shop|space|feeds)
     data-screen  -> which controller runs           (login|onboarding|dashboard|...)

   Messages and video calling are deliberately stubbed. Search for
   "STUB — LATER" to find every place that has to be filled in when
   Firebase live messaging and the video-call provider go in.
   ========================================================================== */

(function (window, document) {
  'use strict';

  /* ======================================================================
     0. CONFIG
     ====================================================================== */

  var CFG = {
    /* Shopify App Proxy. Set up in the Shopify app as:
         subpath prefix: apps
         subpath:        comnito
         proxy URL:      https://comnito-worker.mailm2chub.workers.dev
       Everything below then goes through your own domain, so no CORS. */
    API: '/apps/comnito',

    /* The App Proxy prefix, which is also where the shared page is
       served from. One constant, so changing the proxy in Shopify is a
       one-line change here rather than a hunt. */
    SHARE_BASE: '/apps/comnito',

    /* Direct Worker URL. Only used if the proxy is not configured yet.
       Set USE_PROXY to false to hit the Worker straight. Requires the
       Worker's CORS origin to match your storefront domain. */
    WORKER: 'https://comnito-worker.mailm2chub.workers.dev',
    USE_PROXY: true,

    /* Shopify page handles. Change these ONLY if you name your pages
       differently when you create them. */
    PAGES: {
      login:      '/pages/comnito-login',
      onboarding: '/pages/comnito-onboarding',
      home:       '/pages/comnito-home',
      nearby:     '/pages/comnito-nearby',
      feeds:      '/pages/comnito-feeds',
      space:      '/pages/comnito-my-space',
      settings:   '/pages/comnito-settings',
      addListing: '/pages/comnito-add-listing',
      gallery:    '/pages/comnito-gallery',
      messages:   '/pages/comnito-messages',
      requests:   '/pages/comnito-requests',
      /* Notifications share the Requests page until they have
         their own — one destination, named honestly. */
      notifications: '/pages/comnito-requests',
      /* The pages people SHARE. Their handles are the short ones, so a
         link reads comnito.com/pages/my-store?handle=lakshmana-tailor
         rather than comnito.com/pages/comnito-store?handle=vj-548602.
         Rename these three pages in Shopify to match. */
      store:      '/pages/my-store',
      services:   '/pages/my-services',
      personal:   '/pages/my-personal',
      /* The Shop tab leaves Comnito for the Shopify storefront. Any absolute
         URL here opens in a new tab so people keep their place in the app. */
      shop:       'https://1be1sp-90.myshopify.com'
    },

    /* Things that are built but not switched on yet. Nothing is deleted —
       flip a flag back to true and the feature returns exactly as it was.

       bottomNav — the five-tab bar. Off until the tabs are wired end to end.
       otpLogin  — the mobile number + WhatsApp code path. Off until the OTP
                   sender is live; Google is the way in meanwhile. */
    FEATURES: {
      bottomNav: false,
      otpLogin:  false
    },

    TOKEN_KEY: 'comnito_token',
    CACHE_KEY: 'comnito_cache',

    /* One monthly plan. The Worker enforces all of this — these values
       only shape the copy, and if they ever disagree the Worker wins and
       the UI is what is wrong. */
    PRICING: {
      baseMonthly:      49,  /* one space, publishing included */
      extraSpace:       30,  /* per month, per additional space */
      addonMonthly:     20,  /* listing slots, gallery storage, video calls */
      trialDays:        30,
      domainMonthly:    99,
      videoPerCall:     49   /* per 30 min, paid by the visitor */
    },

    /* ---- the pricing sentences ------------------------------------
       One place, because these were drifting: three screens said three
       different things about what ₹49 buys.

       ₹49 is a monthly plan now, not a one-time charge. Publishing is
       part of it, so nothing is sold at the moment somebody presses
       Publish — the plan is sold once and the app then gets out of the
       way. */
    COPY: {
      plan:
        '₹49 a month covers one space, with publishing included. ' +
        'No commission, ever.',
      extraSpace:
        'Another space — a Store alongside your Services, say — is ' +
        '₹30/month on the same bill.',
      addons:
        'Add-ons are ₹20/month each: more listing slots, more gallery ' +
        'storage, or video calling. Add and drop them whenever you like.',
      drafts:
        'Drafts are free and unlimited. Save now, publish whenever you are ' +
        'ready — only published listings appear on your page.',
      passwordShare:
        'You can also put a password on your page and share it only with ' +
        'the people who ask.',
      domain:
        'And at ₹99 you can put your own domain on it, if you want one.',
      photoStorage:
        'Photos come with the plan — 100 MB, and anything attached to a ' +
        'listing does not count. Another 100 MB is ₹20/month.',
      videoStorage:
        'Video storage is ₹20/month for 100 MB, listing videos included. ' +
        'Add it when you want video, drop it when you do not.'
    },

    /* One line per space type, used wherever a space has nothing else
       to say about itself yet. */
    SPACE_BLURB: {
      store:    'Products you sell',
      services: 'Work you take bookings for',
      personal: 'Your personal page',
      social:   'Your community page'
    },

    /* Listings you may have LIVE at once, per pack, by space type.
       Drafts are unlimited and always free. */
    /* Space type -> the Worker's main listing type, which is how the
       taxonomy is keyed. store is called business there. */
    MAIN_TYPE: { store: 'business', services: 'services', personal: 'personal' },

    QUOTAS: { store: 10, services: 5, personal: 3, social: 3 },

    LIMITS: {
      galleryPhotosPerListing: 5,
      galleryVideosPerListing: 1,
      photoBytes: 5 * 1024 * 1024,     /* 5 MB */
      videoBytes: 25 * 1024 * 1024,    /* 25 MB */
      maxListingsPerSpace: 200         /* drafts included; an abuse ceiling */
    }
  };

  function base() { return CFG.USE_PROXY ? CFG.API : CFG.WORKER; }

  /* ======================================================================
     1. TINY HELPERS
     ====================================================================== */

  /* Stamp the feature flags on <html> the moment this script runs — before
     first paint, since it is deferred rather than async. CSS keyed on these
     classes hides the switched-off parts with no flash of the old UI, and
     CFG.FEATURES stays the single place to flip them back on. */
  try {
    var _r = document.documentElement;
    _r.classList.toggle('comnito-no-nav', !CFG.FEATURES.bottomNav);
    _r.classList.toggle('comnito-no-otp', !CFG.FEATURES.otpLogin);
  } catch (e) {}

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  /* Resolve a mount point. Pages carry data-comnito="name" hooks so the
     controllers never depend on the prototype's own element ids. Falls back
     to a plain id of the same name. */
  /* Failures that should not interrupt the user still must not vanish.
     Every `.catch(quiet('...'))` logs one line with the same prefix, so
     "nothing happened and there was nothing in the console" stops being a
     possible bug report. Pass a label describing what was being loaded. */
  function quiet(what) {
    return function (e) {
      console.warn('[Comnito] ' + what + ' failed:', (e && e.message) || e);
    };
  }

  /* Anything the app injects has to land inside .comnito-app — that is the
     scope the theme isolation layer covers. On <body> it picks up Eurus's
     own heading and button styling instead. */
  function appRoot() {
    return document.querySelector('.comnito-app') ||
           document.querySelector('.comnito-app-wrapper') ||
           document.body;
  }

  function hook(name) {
    return document.querySelector('[data-comnito="' + name + '"]') ||
           document.getElementById(name);
  }
  /* Replace only a node's leading text, so badges and icons sitting next
     to the label survive the update. */
  function setText(node, value) {
    if (!node) return;
    var first = node.firstChild;
    if (first && first.nodeType === 3) first.nodeValue = value + ' ';
    else node.insertBefore(document.createTextNode(value + ' '), first || null);
  }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  /* The handle a name will become, for the live preview in onboarding.
     The Worker makes the real one — this only has to look right. */
  function slugify(v) {
    return String(v || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'your-name';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function money(n) {
    var v = Number(n || 0);
    try {
      return '₹' + v.toLocaleString('en-IN',
        { minimumFractionDigits: 0, maximumFractionDigits: v % 1 === 0 ? 0 : 2 });
    } catch (e) {
      return '₹' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
    }
  }
  function ago(iso) {
    if (!iso) return '';
    var s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + 'm ago';
    if (s < 86400) return Math.floor(s / 3600) + 'h ago';
    if (s < 604800) return Math.floor(s / 86400) + 'd ago';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
  function qs(name) {
    return new URLSearchParams(location.search).get(name) || '';
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var a = arguments, c = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(c, a); }, ms || 300);
    };
  }

  /* ======================================================================
     2. TOAST
     ====================================================================== */

  var toastEl = null, toastTimer = null;

  function toast(msg, ms) {
    if (!toastEl) {
      toastEl = $('#toast');
      if (!toastEl) {
        toastEl = el('div', 'toast');
        toastEl.id = 'toast';
        appRoot().appendChild(toastEl);
      }
    }
    toastEl.textContent = msg;
    toastEl.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('on'); }, ms || 2600);
  }

  /* ======================================================================
     2b. SHEETS — the app's own dialogs

     These replace window.prompt / window.confirm. Native dialogs were the
     wrong tool here for three reasons: on a phone they are a system alert
     with no styling and no context, some mobile browsers suppress repeated
     ones outright (so the app appears to do nothing), and asking someone to
     TYPE "store, services or personal" is not a choice, it is a quiz.

     sheet() returns a Promise: null if the person backs out, otherwise
     { choice, values } — the picked option and any field values.
     ====================================================================== */

  function sheet(opts) {
    opts = opts || {};
    return new Promise(function (resolve) {
      var wrap = document.createElement('div');
      wrap.className = 'cmodal';
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');

      var choices = (opts.choices || []).map(function (c, i) {
        return '<button type="button" class="cmodal-choice" data-value="' + esc(c.value) + '"' +
          (i === 0 ? ' aria-pressed="true"' : ' aria-pressed="false"') + '>' +
          '<span class="cmodal-choice-t">' + esc(c.label) + '</span>' +
          (c.hint ? '<span class="cmodal-choice-h">' + esc(c.hint) + '</span>' : '') +
          '</button>';
      }).join('');

      var fields = (opts.fields || []).map(function (f) {
        return '<label class="field-label" for="cm-' + esc(f.name) + '">' + esc(f.label) + '</label>' +
          '<div class="field"><input class="in" id="cm-' + esc(f.name) + '" name="' + esc(f.name) + '"' +
          ' type="' + esc(f.type || 'text') + '"' +
          (f.inputmode ? ' inputmode="' + esc(f.inputmode) + '"' : '') +
          ' placeholder="' + esc(f.placeholder || '') + '"' +
          ' value="' + esc(f.value == null ? '' : String(f.value)) + '"></div>';
      }).join('');

      wrap.innerHTML =
        '<div class="cmodal-scrim" data-close="1"></div>' +
        '<div class="cmodal-card">' +
          '<div class="grip" aria-hidden="true"></div>' +
          '<h2 class="cmodal-t">' + esc(opts.title || '') + '</h2>' +
          (opts.note ? '<p class="cmodal-n">' + esc(opts.note) + '</p>' : '') +
          (choices ? '<div class="cmodal-choices">' + choices + '</div>' : '') +
          fields +
          '<div class="cmodal-actions">' +
            '<button type="button" class="btn btn-ghost" data-close="1">' +
              esc(opts.cancel || 'Cancel') + '</button>' +
            '<button type="button" class="btn ' +
              (opts.danger ? 'btn-danger' : 'btn-primary') + '" data-ok="1">' +
              esc(opts.ok || 'Continue') + '</button>' +
          '</div>' +
        '</div>';

      /* Inside .comnito-app, not on <body>. The theme isolation layer is
         scoped to .comnito-app, so a sheet appended to the body inherits the
         host theme's uppercase headings and button skin instead of ours. */
      appRoot().appendChild(wrap);
      document.body.style.overflow = 'hidden';

      var first = wrap.querySelector('.in') || wrap.querySelector('[data-ok]');
      if (first) setTimeout(function () { try { first.focus(); } catch (e) {} }, 30);

      function close(result) {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = '';
        wrap.remove();
        resolve(result);
      }
      function onKey(e) {
        if (e.key === 'Escape') close(null);
        if (e.key === 'Enter' && !opts.choices) submit();
      }
      function submit() {
        var picked = wrap.querySelector('.cmodal-choice[aria-pressed="true"]');
        var values = {};
        $$('.in', wrap).forEach(function (i) { values[i.name] = i.value.trim(); });
        var missing = (opts.fields || []).filter(function (f) {
          return f.required !== false && !values[f.name];
        })[0];
        if (missing) { toast('Please fill in ' + missing.label.toLowerCase()); return; }
        close({ choice: picked ? picked.dataset.value : null, values: values });
      }

      wrap.addEventListener('click', function (e) {
        if (e.target.closest('[data-close]')) return close(null);
        if (e.target.closest('[data-ok]')) return submit();
        var c = e.target.closest('.cmodal-choice');
        if (c) {
          $$('.cmodal-choice', wrap).forEach(function (x) {
            x.setAttribute('aria-pressed', 'false');
          });
          c.setAttribute('aria-pressed', 'true');
        }
      });
      document.addEventListener('keydown', onKey);
    });
  }

  /* A yes/no sheet. Same shape as confirm(), but it is ours and it renders. */
  function confirmSheet(title, note, okLabel, danger) {
    return sheet({ title: title, note: note, ok: okLabel || 'Yes', danger: danger })
      .then(function (r) { return !!r; });
  }

  /* ======================================================================
     3. SESSION
     ====================================================================== */

  var Session = {
    get token() {
      try { return localStorage.getItem(CFG.TOKEN_KEY) || ''; }
      catch (e) { return window.__comnitoToken || ''; }
    },
    set token(v) {
      try {
        if (v) localStorage.setItem(CFG.TOKEN_KEY, v);
        else localStorage.removeItem(CFG.TOKEN_KEY);
      } catch (e) { window.__comnitoToken = v; }
    },
    get me() {
      try { return JSON.parse(sessionStorage.getItem(CFG.CACHE_KEY) || 'null'); }
      catch (e) { return null; }
    },
    set me(v) {
      try { sessionStorage.setItem(CFG.CACHE_KEY, JSON.stringify(v || null)); }
      catch (e) {}
    },
    clear: function () {
      this.token = '';
      this.me = null;
    },
    requireLogin: function (returnTo) {
      if (this.token) return true;
      var r = returnTo || location.pathname + location.search;
      location.href = CFG.PAGES.login + '?return=' + encodeURIComponent(r);
      return false;
    }
  };

  /* ======================================================================
     4. API CLIENT
     ====================================================================== */

  function apiUrl(path, params) {
    var u = base() + path;
    var p = new URLSearchParams();
    if (Session.token) p.set('token', Session.token);
    Object.keys(params || {}).forEach(function (k) {
      if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
        p.set(k, params[k]);
      }
    });
    var q = p.toString();
    return q ? u + (u.indexOf('?') > -1 ? '&' : '?') + q : u;
  }

  function request(method, path, opts) {
    opts = opts || {};
    var init = { method: method, headers: {} };
    var url;

    if (method === 'GET' || method === 'DELETE') {
      url = apiUrl(path, opts.params);
    } else if (opts.form) {
      url = apiUrl(path, opts.params);
      init.body = opts.form;                       /* FormData: no Content-Type */
    } else {
      url = apiUrl(path, opts.params);
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(
        Object.assign({ token: Session.token }, opts.body || {})
      );
    }

    return fetch(url, init)
      .then(function (r) {
        return r.text().then(function (text) {
          var data;
          try { data = text ? JSON.parse(text) : {}; }
          catch (e) { data = { success: false, error: 'Server returned: ' + text.slice(0, 200) }; }

          if (r.status === 401) {
            Session.clear();
            if (!opts.silent) {
              /* Without a counter this is an infinite loop: sign in, land on
                 home, /app/me returns 401, get sent back to login, sign in
                 again. The user sees the login page over and over and no
                 explanation. Two bounces is enough to know it is not a
                 stale token. */
              var n = 0;
              try {
                n = Number(sessionStorage.getItem('comnito_401') || 0) + 1;
                sessionStorage.setItem('comnito_401', String(n));
              } catch (e) { n = 1; }

              if (n > 2) {
                toast(data.error ||
                  'Signed in, but this account could not be loaded. ' +
                  'Please tell us what you see.', 6000);
                console.error('[Comnito] repeated 401 from', path, data);
              } else {
                toast('Session expired. Please login again.');
                setTimeout(function () { Session.requireLogin(); }, 900);
              }
            }
          } else if (r.ok) {
            try { sessionStorage.removeItem('comnito_401'); } catch (e) {}
          }
          if (r.status === 402 && data.upgrade) {
            /* A paywall, not a failure. Rewrite the message so whatever the
               caller toasts already reads as an offer with a price. */
            var up = data.upgrade;
            var msg = data.error || (up.label + ' is a paid feature.');
            if (up.price_inr) msg += '  (' + money(up.price_inr) + ')';
            var pErr = new Error(msg);
            pErr.status = 402;
            pErr.upgrade = up;
            pErr.data = data;
            window.Comnito && (window.Comnito.lastUpgrade = up);
            throw pErr;
          }
          if (!r.ok || data.success === false) {
            var err = new Error(data.error || ('HTTP ' + r.status));
            err.status = r.status;
            err.data = data;
            throw err;
          }
          return data;
        });
      });
  }

  var API = {
    get:    function (p, params, o) { return request('GET', p, Object.assign({ params: params }, o)); },
    post:   function (p, body, o)   { return request('POST', p, Object.assign({ body: body }, o)); },
    patch:  function (p, body, o)   { return request('PATCH', p, Object.assign({ body: body }, o)); },
    del:    function (p, params, o) { return request('DELETE', p, Object.assign({ params: params }, o)); },
    upload: function (p, formData)  { return request('POST', p, { form: formData }); },

    /* ---- auth ---------------------------------------------------- */
    sendOtp:   function (phone) { return API.post('/app/otp/send', { phone: phone }); },
    verifyOtp: function (phone, otp) { return API.post('/app/otp/verify', { phone: phone, otp: otp }); },
    me:        function () { return API.get('/app/me'); },
    logout:    function () { return API.post('/app/logout', {}); },
    onboard:   function (data) { return API.post('/app/onboarding', data); },

    /* ---- spaces -------------------------------------------------- */
    spaces:      function () { return API.get('/app/spaces'); },
    createSpace: function (data) { return API.post('/app/spaces', data); },
    updateSpace: function (id, data) {
      return API.patch('/app/spaces/' + encodeURIComponent(id), data);
    },
    publicSpace: function (handle, password) {
      return API.get('/app/public/space', { handle: handle, password: password || '' });
    },

    /* ---- listings ------------------------------------------------ */
    listings:      function (spaceId) { return API.get('/app/listings', { space_id: spaceId }); },
    createListing: function (data) { return API.post('/app/listings', data); },
    updateListing: function (id, data) {
      return API.patch('/app/listings/' + encodeURIComponent(id), data);
    },
    deleteListing: function (id) {
      return API.del('/app/listings/' + encodeURIComponent(id));
    },

    /* ---- gallery ------------------------------------------------- */
    gallery:       function (spaceId) { return API.get('/app/gallery', { space_id: spaceId }); },
    galleryUpload: function (file, kind, spaceId) {
      var fd = new FormData();
      fd.append('token', Session.token);
      fd.append('file', file, file.name);
      fd.append('kind', kind || 'photo');
      if (spaceId) fd.append('space_id', spaceId);
      return API.upload('/app/gallery/upload', fd);
    },
    galleryUpdate: function (id, data) {
      return API.patch('/app/gallery/' + encodeURIComponent(id), data);
    },
    galleryTag: function (mediaId, listingId, on) {
      return API.post('/app/gallery/tag', {
        media_id: mediaId, listing_id: listingId, tagged: on !== false
      });
    },
    galleryDelete: function (id) { return API.del('/app/gallery/' + encodeURIComponent(id)); },

    /* Space pictures are not gallery photos: a cover is furniture, and it
       must never eat one of a listing's five photo slots. */
    spacePicture: function (file, slot, spaceId) {
      var fd = new FormData();
      fd.append('token', Session.token);
      fd.append('file', file, file.name);
      fd.append('slot', slot);
      fd.append('space_id', spaceId);
      return API.upload('/app/space/picture', fd);
    },
    spacePictureRemove: function (slot, spaceId) {
      return API.post('/app/space/picture/remove', { slot: slot, space_id: spaceId });
    },

    /* ---- verification, which gates nothing --------------------------- */
    kyc: function () { return API.get('/app/kyc'); },
    kycSubmit: function (type, value, file) {
      var fd = new FormData();
      fd.append('token', Session.token);
      fd.append('type', type);
      if (value) fd.append('value', value);
      if (file) fd.append('file', file, file.name);
      return API.upload('/app/kyc', fd);
    },

    /* ---- deals (requests + orders) ------------------------------- */
    requests:      function (role, status) { return API.get('/app/requests', { role: role, status: status }); },
    createRequest: function (data) { return API.post('/app/requests', data); },
    updateRequest: function (id, data) {
      return API.patch('/app/requests/' + encodeURIComponent(id), data);
    },

    /* ---- social -------------------------------------------------- */
    follow:    function (targetId, on) { return API.post('/app/follow', { target_id: targetId, follow: on !== false }); },
    followers: function () { return API.get('/app/followers'); },
    following: function () { return API.get('/app/following'); },
    feed:      function (cursor) { return API.get('/app/feed', { cursor: cursor }); },
    /* NOT `post`. There is already an API.post — the generic HTTP verb —
       and a duplicate key in an object literal is legal JavaScript: the
       second one silently wins. When this was called `post`, API.post
       stopped being the HTTP helper and became this, so every write in the
       app (onboarding, create space, create listing, requests, follow,
       report, addons, messages) called it with a path string as its body,
       and posting itself recursed until the stack blew. Reads were fine,
       which is why the app looked alive and did nothing. */
    createPost: function (data) { return API.post('/app/posts', data); },
    like:      function (id) { return API.post('/app/posts/' + encodeURIComponent(id) + '/like', {}); },

    /* ---- discovery ----------------------------------------------- */
    nearby: function (o) { return API.get('/app/nearby', o); },

    /* ---- misc ---------------------------------------------------- */
    report:  function (data) { return API.post('/app/report', data); },
    addons:  function () { return API.get('/app/addons'); },
    entitlements: function () { return API.get('/app/entitlements'); },
    activateAddon: function (key, on) { return API.post('/app/addons/activate', { addon: key, active: on !== false }); },
    taxonomy: function () { return API.get('/app/taxonomy'); },

    /* ---- STUB — LATER: messages + video --------------------------- */
    threads:  function () { return API.get('/app/messages/threads', null, { silent: true }); },
    messages: function (threadId) { return API.get('/app/messages', { thread_id: threadId }, { silent: true }); },
    send:     function (threadId, text) { return API.post('/app/messages', { thread_id: threadId, text: text }); },
    videoStart: function (threadId) { return API.post('/app/video/start', { thread_id: threadId }); }
  };

  /* ======================================================================
     4b. THEME MIRRORING
     Eurus has its own light/dark switch, and its CSS hook is not documented.
     Rather than guess at a class name, read the page's actual background
     and mirror it onto the app. Works whatever the theme signals with, and
     self-corrects when the user flips the switch.
     ====================================================================== */

  function luminance(rgbString) {
    var m = String(rgbString || '').match(/[\d.]+/g);
    if (!m || m.length < 3) return null;
    if (m.length > 3 && Number(m[3]) === 0) return null;   /* transparent */
    return (0.299 * +m[0] + 0.587 * +m[1] + 0.114 * +m[2]) / 255;
  }

  function detectDark() {
    var probes = [document.body, document.documentElement];
    for (var i = 0; i < probes.length; i++) {
      var L = luminance(getComputedStyle(probes[i]).backgroundColor);
      if (L !== null) return L < 0.45;
    }
    try { return matchMedia('(prefers-color-scheme: dark)').matches; }
    catch (e) { return false; }
  }

  /* Comnito no longer mirrors the host theme.

     This used to read the page's background luminance and stamp
     data-comnito-theme="dark" so the app followed Eurus's dark switch. That
     stamp is exactly what turned the cards navy while the warm palette kept
     painting dark text on them. The dark token set is gone from comnito.css,
     so the app is light in every case and the stamp is now only a label for
     diagnose(). detectDark() is kept because diagnose() reports what the host
     theme is doing — it just no longer decides anything. */
  function syncTheme() {
    document.documentElement.setAttribute('data-comnito-theme', 'light');
  }

  function watchTheme() {
    syncTheme();
  }

  /* ======================================================================
     5. SHARED CHROME — bottom nav, top bar, generic controls
     ====================================================================== */

  var NAV = [
    { id: 'home',   label: 'Home',     page: 'home',
      d: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5"/>' },
    { id: 'nearby', label: 'Near You', page: 'nearby',
      d: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>' },
    { id: 'shop',   label: 'Shop',     page: 'shop',
      d: '<path d="M5 8h14l-1 12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>' },
    { id: 'space',  label: 'My Space', page: 'space',
      d: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>' },
    { id: 'feeds',  label: 'Feeds',    page: 'feeds',
      d: '<path d="M4 5h16M4 12h16M4 19h10"/>' }
  ];

  function mountNav(root) {
    var mount = $('#nav', root);
    if (!mount) return;
    if (!CFG.FEATURES.bottomNav) {
      /* .bottomnav is position:sticky, so removing it reclaims its space
         without any layout compensation. */
      mount.remove();
      if (root) root.classList.add('no-bottomnav');
      return;
    }
    var here = (root && root.dataset.page) || document.body.dataset.page || '';
    mount.className = 'bottomnav';
    mount.innerHTML = NAV.map(function (n) {
      return '<button class="navitem' + (n.id === here ? ' is-active' : '') +
        '" data-go="' + n.id + '" data-label="' + n.label + '">' +
        '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + n.d + '</svg>' +
        n.label + '<span class="badge-n" data-badge="' + n.id + '" hidden>0</span>' +
        '</button>';
    }).join('');

    mount.addEventListener('click', function (e) {
      var b = e.target.closest('.navitem');
      if (!b || b.classList.contains('is-active')) return;
      var n = NAV.filter(function (x) { return x.id === b.dataset.go; })[0];
      if (!n) return;
      var url = CFG.PAGES[n.page];
      if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
      else location.href = url;
    });
  }

  function setNavBadge(id, count) {
    var b = document.querySelector('[data-badge="' + id + '"]');
    if (!b) return;
    if (count > 0) { b.textContent = count > 99 ? '99+' : count; b.hidden = false; }
    else b.hidden = true;
  }

  function mountChrome(root) {
    mountNav(root);

    /* Back on a multi-step screen means the previous STEP, not the
       previous page. Leaving add-listing from step 3 threw away two
       screens of typing and dropped somebody in My Space, which is
       what "it goes back to my space, not one by one" meant.

       A screen with steps registers a handler; anything else falls
       through to the browser's own history. */
    var back = $('#back', root);
    if (back) back.addEventListener('click', function () {
      if (typeof stepBack === 'function' && stepBack() === true) return;
      if (history.length > 1) history.back();
      else location.href = CFG.PAGES.home;
    });

    var me = $('#me', root);
    if (me) me.addEventListener('click', function () { location.href = CFG.PAGES.settings; });

    normaliseTopbar(root);

    /* declarative toasts kept from the prototype */
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-toast]');
      if (!t) return;
      e.preventDefault();
      toast(t.getAttribute('data-toast'));
    });

    bindControls(root || document);
  }

  /* ---- the top bar --------------------------------------------------
     Every prototype grew its own set of header icons: the dashboard has
     requests, a bell and an avatar, Feeds has only a bell, My Space has
     only requests. Rather than editing eight pages and having the ninth
     drift again, the header is normalised here — the same three icons in
     the same order, everywhere, each wired to the page it belongs to.

     The logo goes home, which is the one thing every person tries first
     and the one thing it did not do. */
  var TOPBAR_ICONS = [
    { id: 'requests', page: 'requests', label: 'Requests',
      svg: '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
           '<path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>' },
    { id: 'messages', page: 'messages', label: 'Messages',
      svg: '<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-4-.9L3 21l1.9-4.5' +
           'A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.4A8.4 8.4 0 0 1 21 11.5z"/>' },
    { id: 'bell', page: 'notifications', label: 'Notifications',
      svg: '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>' +
           '<path d="M13.7 21a2 2 0 0 1-3.4 0"/>' }
  ];

  function iconButton(spec) {
    var b = el('button', 'iconbtn');
    b.id = spec.id;
    b.type = 'button';
    b.setAttribute('aria-label', spec.label);
    b.innerHTML = '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round">' + spec.svg + '</svg>';
    return b;
  }

  function normaliseTopbar(root) {
    var bar = $('.topbar', root);
    if (!bar) return;

    /* Home. The mark and the wordmark are one target — people aim at
       either half and expect the same thing. */
    var mark = $('.mark', bar), brand = $('.brand', bar);
    [mark, brand].forEach(function (n) {
      if (!n) return;
      n.style.cursor = 'pointer';
      n.setAttribute('role', 'link');
      n.setAttribute('tabindex', '0');
      n.setAttribute('aria-label', 'Comnito home');
      n.addEventListener('click', function () { location.href = CFG.PAGES.home; });
      n.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          location.href = CFG.PAGES.home;
        }
      });
    });

    var avatar = $('.avatar', bar);
    TOPBAR_ICONS.forEach(function (spec) {
      var b = $('#' + spec.id, bar);
      if (!b) {
        b = iconButton(spec);
        if (avatar) bar.insertBefore(b, avatar);
        else bar.appendChild(b);
      }
      if (b.__wired) return;
      b.__wired = true;
      b.addEventListener('click', function () {
        location.href = CFG.PAGES[spec.page] || CFG.PAGES.requests;
      });
    });
  }

  /* ---- switches / segments / chipsets / steppers --------------------
     Each control fires a `comnito:change` event so page controllers can
     persist the value without re-implementing the interaction.          */

  /* Every control gets a stable key even when the prototype markup carries
     no data-name: data-name > id > data-governs > slugified data-label. */
  function ctrlName(node) {
    return node.dataset.name || node.id || node.dataset.governs ||
      String(node.dataset.label || 'setting').toLowerCase()
        .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function fire(node, detail) {
    node.dispatchEvent(new CustomEvent('comnito:change', {
      bubbles: true, detail: detail
    }));
  }

  function bindControls(root) {
    $$('.switch', root).forEach(function (sw) {
      if (sw.__bound) return; sw.__bound = true;
      sw.addEventListener('click', function () {
        if (sw.disabled) return;
        var on = sw.getAttribute('aria-checked') !== 'true';
        sw.setAttribute('aria-checked', on ? 'true' : 'false');

        var gov = sw.dataset.governs;
        if (gov) gate(gov, on);

        var msg = (on && sw.dataset.on) ? sw.dataset.on
                : (!on && sw.dataset.off) ? sw.dataset.off
                : (sw.dataset.label || 'Setting') + (on ? ' on' : ' off');
        if (!sw.dataset.quiet) toast(msg);
        fire(sw, { kind: 'switch', name: ctrlName(sw), value: on });
      });
      if (sw.dataset.governs && sw.getAttribute('aria-checked') !== 'true') {
        gate(sw.dataset.governs, false);
      }
    });

    $$('.segment', root).forEach(function (seg) {
      if (seg.__bound) return; seg.__bound = true;
      seg.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b) return;
        $$('button', seg).forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        var val = b.dataset.value || b.textContent.trim();
        if (!seg.dataset.quiet) {
          toast((seg.dataset.label ? seg.dataset.label + ': ' : '') + b.textContent.trim());
        }
        fire(seg, { kind: 'segment', name: ctrlName(seg), value: val });
      });
    });

    $$('.chipset', root).forEach(function (set) {
      if (set.__bound) return; set.__bound = true;
      set.addEventListener('click', function (e) {
        var c = e.target.closest('.chip');
        if (!c || c.disabled) return;
        if (set.hasAttribute('data-single')) {
          $$('.chip', set).forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          c.setAttribute('aria-pressed', 'true');
        } else {
          c.setAttribute('aria-pressed',
            c.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        }
        var on = $$('.chip[aria-pressed="true"]', set).map(function (x) {
          return x.dataset.name || x.dataset.value || x.textContent.trim();
        });
        if (!set.dataset.quiet) {
          toast((set.dataset.label || 'Selected') + ': ' + (on.length ? on.join(', ') : 'none'));
        }
        fire(set, {
          kind: 'chipset', name: ctrlName(set),
          value: set.hasAttribute('data-single') ? (on[0] || '') : on
        });
      });
    });

    $$('.stepper', root).forEach(function (st) {
      if (st.__bound) return; st.__bound = true;
      var n = $('.n', st),
          min = +st.dataset.min || 0,
          max = +st.dataset.max || 999,
          step = +st.dataset.step || 1;
      st.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b) return;
        var v = parseInt(n.textContent, 10) || 0;
        v = Math.min(max, Math.max(min, v + (b.dataset.dir === 'up' ? step : -step)));
        n.textContent = v;
        var down = $('[data-dir="down"]', st), up = $('[data-dir="up"]', st);
        if (down) down.disabled = v <= min;
        if (up) up.disabled = v >= max;
        if (!st.dataset.quiet) {
          toast((st.dataset.label || 'Set to') + ' ' + v + (st.dataset.unit ? ' ' + st.dataset.unit : ''));
        }
        fire(st, { kind: 'stepper', name: ctrlName(st), value: v });
      });
    });

    $$('[data-copy]', root).forEach(function (b) {
      if (b.__bound) return; b.__bound = true;
      b.addEventListener('click', function () {
        var text = b.dataset.copy || b.getAttribute('data-copy-from')
          ? ($(b.getAttribute('data-copy-from') || '') || {}).textContent
          : b.dataset.copy;
        text = b.dataset.copy || text || '';
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(
            function () { toast('Link copied'); },
            function () { toast(text); }
          );
        } else { toast(text); }
      });
    });
  }

  function gate(name, on) {
    $$('[data-gated="' + name + '"]').forEach(function (n) {
      n.style.opacity = on ? '' : '.45';
      n.style.pointerEvents = on ? '' : 'none';
      n.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  /* ======================================================================
     6. RENDER HELPERS shared by several screens
     ====================================================================== */

  function empty(msg, sub) {
    return '<div class="empty"><p class="empty-t">' + esc(msg) + '</p>' +
           (sub ? '<p class="empty-s">' + esc(sub) + '</p>' : '') + '</div>';
  }

  /* The stylesheet keys the per-space colour tokens off short suffixes
     that predate the long type names. One map, so the two never drift. */
  var SPACE_CLASS = { store: 'store', services: 'serv', personal: 'pers',
                      social: 'pers' };

  function spaceTone(type) {
    var t = String(type || '').toLowerCase();
    if (t.indexOf('serv') === 0) return 'services';
    if (t.indexOf('pers') === 0) return 'personal';
    return 'store';
  }

  function listingCard(item, opts) {
    opts = opts || {};
    var img = item.image_url || item.product_image_1_url || '';
    var price = item.selling_price_inr || item.price;
    return '' +
      '<article class="lcard" data-id="' + esc(item.id) + '">' +
        '<div class="lcard-media">' +
          (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">'
               : '<span class="lcard-ph"></span>') +
        '</div>' +
        '<div class="lcard-body">' +
          '<h3 class="lcard-t">' + esc(item.product_name || item.title || 'Untitled') + '</h3>' +
          (item.subcategory ? '<p class="lcard-c">' + esc(item.subcategory) + '</p>' : '') +
          (price != null && price !== '' ? '<p class="lcard-p">' + money(price) + '</p>' : '') +
        '</div>' +
        (opts.actions !== false
          ? '<div class="lcard-act">' +
              '<button class="ibtn" data-act="edit" aria-label="Edit">Edit</button>' +
              '<button class="ibtn" data-act="toggle" aria-label="Publish">' +
                (String(item.status || 'active') === 'active' ? 'Hide' : 'Publish') +
              '</button>' +
            '</div>'
          : '') +
      '</article>';
  }

  /* ======================================================================
     7. SCREENS
     ====================================================================== */


  /* ======================================================================
     LISTING FIELD SPEC

     Every question the add-listing form can ask, in one table. The form
     is generated from it, the payload is read back from it, and nothing
     is asked twice.

     Why a table and not markup: the three space types want genuinely
     different questions — a tailor needs MOQ and delivery radius, a
     nurse needs tokens per day and whether they come to you, and a
     person looking for a travel companion needs none of that and no
     price at all. Written as markup that is three near-identical forms
     drifting apart; written as a table it is one form and a filter.

       key    the name saved. Anything in the Worker's detail whitelist
              is sent under details.<key>; the rest are top-level.
       type   text | textarea | number | money | select | chips | switch
       for    which space types ask it: 'store', 'services', 'personal'
       hint   the sentence under the field. Say why, not what.
       req    blocks publishing without it
       when   another field's key that must be truthy first
     ====================================================================== */

  var LISTING_FIELDS = {

    /* ---- basics, asked of everything ------------------------------- */
    basics: [
      { key: 'product_name', type: 'text', top: true, req: true,
        label: { store: 'What are you selling?',
                 services: 'What is the service?',
                 personal: 'Give this a title' },
        for: 'store services personal',
        placeholder: { store: 'Cotton kurta, ready-made',
                       services: 'Blouse stitching',
                       personal: 'Evening walks in Race Course' },
        hint: 'Say it plainly. People search the way they speak.' },

      { key: 'description', type: 'textarea', top: true,
        label: { store: 'Description',
                 services: 'What the work involves',
                 personal: 'About this' },
        for: 'store services personal',
        placeholder: { store: 'Size, colour, material, what is included…',
                       services: 'What you do, what is included, how long it takes…',
                       personal: 'What you have in mind, and what you are hoping for' },
        hint: { store: 'The questions you end up answering on every call. ' +
                       'Write them once here.',
                services: 'The questions you end up answering on every call. ' +
                          'Write them once here.',
                personal: 'A few honest lines do more than a long list.' } },

      { key: 'highlight', type: 'text',
        label: 'One line people see first', for: 'store services personal',
        placeholder: { store: 'Handloom cotton · free size',
                       services: 'Ready in 4 days · lining extra',
                       personal: 'Weekends · Coimbatore and around' },
        hint: 'Shown under the title in search results.' },

      { key: 'tags', type: 'text', top: true,
        label: 'Search words', for: 'store services personal',
        placeholder: { store: 'cotton, kurta, handloom, free size',
                       services: 'silk, bridal, aari work, same day',
                       personal: 'walking, morning, coimbatore, company' },
        hint: 'Separated by commas. Brand names, materials, and the local ' +
              'word for the same thing.' },

      { key: 'brand', type: 'text',
        label: 'Brand or make', for: 'store',
        placeholder: 'Prestige, Samsung, own label' },

      { key: 'condition', type: 'select', for: 'store',
        label: 'Condition',
        options: ['New', 'Like new', 'Used — good', 'Used — fair', 'Refurbished'] },

      { key: 'unit', type: 'select', for: 'store',
        label: 'Sold by',
        options: ['Piece', 'Set', 'Kg', 'Gram', 'Litre', 'Metre', 'Dozen', 'Hour', 'Day'] },
    ],

    /* ---- money. Deliberately absent for personal --------------------
       There is no price on a personal listing. Somebody looking for a
       travel companion is not selling anything, and a number next to a
       person is the one thing that would turn this into a marketplace
       for people. */
    money: [
      { key: 'cost_per_item_inr', type: 'money',
        label: 'Your cost', for: 'store',
        hint: 'What it costs you. Only ever visible to you.' },

      { key: 'margin_percent', type: 'number', suffix: '%',
        label: 'Margin you want', for: 'store', value: 30,
        hint: 'The selling price below is worked out from this. ' +
              'Change either one.' },

      /* Not required. A tailor who quotes per job, and a consultant who
         will not put a number on a page, both had a valid listing
         refused for a price they do not have. Leave it empty and the
         listing reads "Ask for price", which is what they would say
         on the phone anyway. */
      { key: 'selling_price_inr', type: 'money', top: true,
        label: { store: 'Selling price', services: 'What you charge' },
        for: 'store services',
        hint: 'What the customer pays — Comnito takes no commission, so ' +
              'nothing is deducted from this. Leave it empty to show ' +
              '"Ask for price" instead.' },

      { key: 'mrp_inr', type: 'money',
        label: 'MRP, if printed', for: 'store',
        hint: 'Shown struck through, so a discount reads as a discount.' },

      { key: 'wholesale_available', type: 'switch',
        label: 'I also sell wholesale', for: 'store' },

      { key: 'wholesale_price_inr', type: 'money', top: true,
        label: 'Wholesale price', for: 'store', when: 'wholesale_available' },

      { key: 'moq', type: 'number',
        label: 'Minimum quantity', for: 'store', when: 'wholesale_available',
        hint: 'The smallest wholesale order you will take.' },

      { key: 'gst_percent', type: 'number', suffix: '%',
        label: 'GST rate', for: 'store services',
        hint: 'Appears on the bill if you have added a GST number.' },
    ],

    /* ---- how it is sold --------------------------------------------- */
    terms: [
      { key: 'sell_on_comnito_shop', type: 'switch',
        label: 'Sell this all over India on the Comnito shop', for: 'store',
        hint: 'As well as locally. You post it; the customer pays the ' +
              'courier. Turn it off to stay local only.' },

      { key: 'pre_order', type: 'switch',
        label: 'Take pre-orders', for: 'store services',
        hint: 'For something you make after the order, not off a shelf.' },

      { key: 'pre_order_days', type: 'number', suffix: ' days',
        label: 'Ready in', for: 'store services', when: 'pre_order' },

      { key: 'made_to_order', type: 'switch',
        label: 'Made to order', for: 'store' },

      { key: 'return_days', type: 'number', suffix: ' days',
        label: 'Returns accepted within', for: 'store',
        hint: 'Leave empty if you do not take returns. Saying so plainly ' +
              'saves an argument later.' },

      { key: 'warranty', type: 'text',
        label: 'Warranty', for: 'store', placeholder: '6 months on the motor' },
    ],

    /* ---- services --------------------------------------------------- */
    service: [
      { key: 'specialization', type: 'text',
        label: 'What you specialise in', for: 'services',
        placeholder: 'Bridal blouses, aari work',
        hint: 'The thing people should come to you for specifically.' },

      { key: 'experience_years', type: 'number', suffix: ' years',
        label: 'Years doing this', for: 'services' },

      { key: 'service_duration', type: 'text', top: true,
        label: 'How long it takes', for: 'services', placeholder: '45 minutes' },

      { key: 'available_hours', type: 'text', top: true,
        label: 'Hours you work', for: 'services', placeholder: '9am – 7pm, closed Sunday' },

      { key: 'at_our_place', type: 'switch',
        label: 'Customer comes to me', for: 'services' },

      { key: 'at_customer_place', type: 'switch',
        label: 'I go to the customer', for: 'services' },

      { key: 'visit_charge_inr', type: 'money',
        label: 'Visiting charge', for: 'services', when: 'at_customer_place',
        hint: 'Charged on top, for the trip. Leave empty if you do not charge.' },

      { key: 'service_area', type: 'text',
        label: 'Areas you cover', for: 'services', when: 'at_customer_place',
        placeholder: 'RS Puram, Peelamedu, Saibaba Colony' },

      { key: 'appointment_required', type: 'switch',
        label: 'By appointment only', for: 'services' },

      { key: 'token_system', type: 'switch',
        label: 'I work on a token system', for: 'services',
        hint: 'A fixed number of people a day, first come first served.' },

      { key: 'tokens_per_day', type: 'number',
        label: 'Tokens a day', for: 'services', when: 'token_system' },

      { key: 'daily_capacity', type: 'number',
        label: 'How many a day you can take', for: 'services' },

      { key: 'languages', type: 'chips',
        label: 'Languages you speak', for: 'services personal',
        options: ['Tamil', 'English', 'Hindi', 'Malayalam', 'Telugu', 'Kannada', 'Other'] },
    ],

    /* ---- personal ---------------------------------------------------
       Written as questions about you and what you are looking for,
       because that is the whole content of a personal listing. */
    personal: [
      { key: 'gender', type: 'select', for: 'personal',
        label: 'You are', options: ['Woman', 'Man', 'Other', 'Prefer not to say'] },

      { key: 'age', type: 'number', top: true, for: 'personal',
        label: 'Your age',
        hint: 'Personal listings are 18+. Your exact age is shown as a range.' },

      { key: 'looking_for', type: 'select', for: 'personal',
        label: 'Looking for',
        options: ['Women', 'Men', 'Anyone', 'Couples', 'A group'] },

      { key: 'age_group', type: 'select', for: 'personal',
        label: 'Age range you are looking for',
        options: ['18–25', '25–35', '35–45', '45–60', '60+', 'No preference'] },

      { key: 'relationship_status', type: 'select', for: 'personal',
        label: 'Your status',
        options: ['Single', 'Divorced', 'Widowed', 'Separated', 'Prefer not to say'] },

      { key: 'living_with', type: 'select', for: 'personal',
        label: 'You live', options: ['Alone', 'With family', 'With friends', 'With children', 'Prefer not to say'] },

      { key: 'profession', type: 'text', for: 'personal', label: 'What you do' },
      { key: 'education', type: 'text', for: 'personal', label: 'Education' },
      { key: 'mother_tongue', type: 'text', for: 'personal', label: 'Mother tongue' },

      { key: 'height_cm', type: 'number', suffix: ' cm',
        label: 'Height', for: 'personal' },

      { key: 'diet', type: 'select', for: 'personal',
        label: 'Food', options: ['Vegetarian', 'Non-vegetarian', 'Eggetarian', 'Vegan', 'Jain'] },

      { key: 'smoking', type: 'select', for: 'personal',
        label: 'Smoking', options: ['No', 'Occasionally', 'Yes'] },

      { key: 'drinking', type: 'select', for: 'personal',
        label: 'Drinking', options: ['No', 'Socially', 'Yes'] },

      { key: 'activity', type: 'text', top: true, for: 'personal',
        label: 'What you want company for',
        placeholder: 'A morning walk, a trip to Ooty, evening coffee' },

      { key: 'place_to_meet', type: 'text', for: 'personal',
        label: 'Where you would like to meet',
        placeholder: 'A café in RS Puram, somewhere public' },

      { key: 'prefer_public_place', type: 'switch', for: 'personal',
        label: 'First meeting in a public place only', value: true,
        hint: 'Shown on your listing. It sets the expectation before ' +
              'anybody messages you, which is the point.' },

      { key: 'about_me', type: 'textarea', for: 'personal',
        label: 'About you',
        hint: 'A few honest lines do more than a long list.' },

      { key: 'requirements', type: 'textarea', top: true, for: 'personal',
        label: 'What you are hoping for',
        placeholder: 'Someone easy to talk to, around my age, from Coimbatore' },
    ],

    /* ---- control, the last step ------------------------------------- */
    control: [
      { key: 'stock_quantity', type: 'number', top: true,
        label: 'Stock', for: 'store',
        hint: 'Counts down as orders come in.' },

      { key: 'inventory_tracked', type: 'switch',
        label: 'Track stock for me', for: 'store', value: true },

      { key: 'low_stock_alert', type: 'number',
        label: 'Tell me when stock drops to', for: 'store',
        when: 'inventory_tracked' },

      { key: 'delivery_available', type: 'switch', top: true,
        label: 'I deliver', for: 'store services' },

      { key: 'delivery_radius_km', type: 'number', suffix: ' km',
        label: 'Deliver within', for: 'store services', when: 'delivery_available',
        hint: 'Anyone further away sees pickup only.' },

      { key: 'delivery_partner', type: 'chips',
        label: 'How it gets there', for: 'store services', when: 'delivery_available',
        options: ['I deliver myself', 'Porter', 'Courier', 'Customer picks up'] },

      { key: 'delivery_charge_inr', type: 'money',
        label: 'Delivery charge', for: 'store services', when: 'delivery_available',
        hint: 'A flat charge. Leave empty for free delivery.' },

      { key: 'free_delivery_above_inr', type: 'money',
        label: 'Free delivery above', for: 'store services', when: 'delivery_available' },

      { key: 'allow_negotiation', type: 'switch', top: true,
        label: 'Price is negotiable', for: 'store services' },

      { key: 'show_price', type: 'switch', value: true,
        label: 'Show the price on the listing', for: 'store services',
        hint: 'Turn off and people see "Ask for price" instead.' },

      { key: 'show_whatsapp', type: 'switch', value: true,
        label: 'Show a WhatsApp button', for: 'store services personal' },

      { key: 'nearby_discovery', type: 'switch', top: true, value: true,
        label: 'Show this in Near You', for: 'store services personal',
        hint: 'How people around you find it. Free.' },

      { key: 'visible_outside', type: 'switch', value: true,
        label: 'Visible on my public page', for: 'store services personal',
        hint: 'Turn off to keep it in your dashboard only — useful for ' +
              'something you are still deciding about.' },
    ]
  };

  var LISTING_GROUPS = [
    { id: 'basics',   host: 'basicsHost',  title: 'The basics' },
    { id: 'money',    host: 'detailsHost', title: 'Price' },
    { id: 'terms',    host: 'detailsHost', title: 'How you sell it' },
    { id: 'service',  host: 'detailsHost', title: 'How the work happens' },
    { id: 'personal', host: 'detailsHost', title: 'About you' },
    { id: 'control',  host: 'controlHost', title: 'Delivery and visibility' }
  ];

  function listingFieldsFor(group, spaceType) {
    return (LISTING_FIELDS[group] || []).filter(function (f) {
      return f.for.split(' ').indexOf(spaceType) > -1;
    });
  }

  /* ---- rendering one field ------------------------------------------
     Every control the spec can name, written once. A field is a label,
     an input, and a sentence — in that order, always, so the form reads
     the same way all the way down however different the questions are. */
  /* A label, placeholder or hint may be one string for every listing
     type, or an object keyed by type. "Size, condition, what is
     included" is right above a kurta and absurd above a person, so the
     ones that differ are written out per type rather than made vague
     enough to cover both. */
  function fieldText(v, spaceType) {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    return v[spaceType] || v.store || v.services || v.personal || '';
  }

  function renderField(f, value, spaceType) {
    var id = 'lf-' + f.key;
    var val = value === undefined || value === null ? f.value : value;
    var label = fieldText(f.label, spaceType);
    var placeholder = fieldText(f.placeholder, spaceType);
    var hint = fieldText(f.hint, spaceType);
    var body = '';

    if (f.type === 'textarea') {
      body = '<textarea class="fin" id="' + id + '" data-f="' + esc(f.key) + '"' +
        ' placeholder="' + esc(placeholder) + '">' +
        esc(val == null ? '' : String(val)) + '</textarea>';

    } else if (f.type === 'select') {
      body = '<select class="fin" id="' + id + '" data-f="' + esc(f.key) + '">' +
        '<option value="">Choose…</option>' +
        (f.options || []).map(function (o) {
          return '<option' + (String(val) === o ? ' selected' : '') + '>' + esc(o) + '</option>';
        }).join('') + '</select>';

    } else if (f.type === 'chips') {
      var on = [].concat(val || []);
      body = '<div class="chipset" data-name="' + esc(f.key) + '" data-quiet="1"' +
        ' data-label="' + esc(label) + '">' +
        (f.options || []).map(function (o) {
          return '<button type="button" class="chip" data-value="' + esc(o) + '"' +
            ' aria-pressed="' + (on.indexOf(o) > -1 ? 'true' : 'false') + '">' +
            esc(o) + '</button>';
        }).join('') + '</div>';

    } else if (f.type === 'switch') {
      /* A switch is its own row: the label sits beside it, not above,
         because a switch with a label stacked on top reads as a heading
         with a stray control next to it. */
      return '<div class="fswitch" data-fwrap="' + esc(f.key) + '">' +
        '<span class="tx"><b>' + esc(label) + '</b>' +
          (hint ? '<span>' + esc(hint) + '</span>' : '') + '</span>' +
        '<button type="button" class="switch" role="switch" data-f="' + esc(f.key) + '"' +
          ' data-name="' + esc(f.key) + '" data-quiet="1"' +
          ' aria-label="' + esc(label) + '"' +
          ' aria-checked="' + (val ? 'true' : 'false') + '"></button>' +
        '</div>';

    } else {
      var kind = f.type === 'money' || f.type === 'number' ? ' num' : '';
      var mode = f.type === 'money' || f.type === 'number'
        ? ' inputmode="numeric"' : '';
      /* A unit belongs inside the box, on the side it is read from: ₹
         before the number, "km" and "%" after it. Left outside the box it
         wrapped onto its own line and read as a stray word. */
      var unit = f.type === 'money'
        ? '<span class="funit pre">₹</span>'
        : (f.suffix ? '<span class="funit post">' + esc(f.suffix.trim()) + '</span>' : '');
      body = '<div class="fwrapin' + (unit ? (f.type === 'money' ? ' has-pre' : ' has-post') : '') + '">' +
        unit +
        '<input class="fin' + kind + '" id="' + id + '" type="text"' + mode +
          ' data-f="' + esc(f.key) + '"' +
          ' placeholder="' + esc(placeholder) + '"' +
          ' value="' + esc(val == null ? '' : String(val)) + '">' +
        '</div>';
    }

    return '<div class="fblock" data-fwrap="' + esc(f.key) + '">' +
      '<label class="flabel" for="' + id + '">' + esc(label) +
        (f.req ? ' <span class="req">*</span>' : '') + '</label>' +
      body +
      (hint ? '<p class="fhint">' + esc(hint) + '</p>' : '') +
      '</div>';
  }


  /* ======================================================================
     PAYING

     Razorpay Checkout collects the card, the UPI pin, everything — in
     its own frame on its own domain. None of it touches Comnito, and
     none of it touches this file.

     What comes back is a signature. That is all the app forwards, and
     the Worker decides what it is worth. Nothing here grants anything,
     which is why none of it is worth attacking.
     ====================================================================== */
  var RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
  var rzpReady = null;

  function loadRazorpay() {
    if (rzpReady) return rzpReady;
    rzpReady = new Promise(function (resolve, reject) {
      if (window.Razorpay) return resolve(window.Razorpay);
      var s = document.createElement('script');
      s.src = RZP_SCRIPT;
      s.onload = function () {
        window.Razorpay ? resolve(window.Razorpay)
                        : reject(new Error('Payments could not load. Check your connection.'));
      };
      s.onerror = function () {
        rzpReady = null;   /* let a retry try again */
        reject(new Error('Payments could not load. Check your connection.'));
      };
      document.head.appendChild(s);
    });
    return rzpReady;
  }

  /* opts: { intent: 'plan' } or { intent: 'grant', grant: key, space_id }
     Resolves true when the Worker has confirmed, false when the person
     closed the window. It never resolves true on the browser's say-so. */
  function pay(opts) {
    opts = opts || {};
    var order = null;

    return API.post('/app/pay/order', {
      intent: opts.intent || 'plan',
      grant: opts.grant || '',
      space_id: opts.space_id || ''
    }).then(function (r) {
      order = r;
      return loadRazorpay();
    }).then(function (Razorpay) {
      return new Promise(function (resolve) {
        var settled = false;

        var rzp = new Razorpay({
          key: order.key_id,
          order_id: order.order_id,
          amount: order.amount_paise,
          currency: order.currency || 'INR',
          name: 'Comnito',
          description: order.description,
          prefill: order.prefill || {},
          theme: { color: '#17324F' },
          handler: function (res) {
            settled = true;
            toast('Confirming…');
            /* The Worker checks the signature. Until it says yes, the
               person has paid but has not been given anything, and the
               app must not pretend otherwise. */
            API.post('/app/pay/confirm', {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature
            }).then(function (done) {
              toast(done.already_settled
                ? 'Already paid — you are all set'
                : 'Paid. ' + (done.plan && done.plan.monthly_inr
                    ? 'Your plan is ' + money(done.plan.monthly_inr) + '/month.'
                    : 'You are all set.'), 4200);
              resolve(true);
            }).catch(function (e) {
              /* The money may well have left their account — the webhook
                 will finish the job. Saying "failed" here would be a lie
                 and would send them to pay again. */
              toast('Payment received. It is taking a moment to confirm — ' +
                    'refresh in a minute and it will be there.', 6500);
              resolve(false);
            });
          },
          modal: {
            ondismiss: function () {
              if (!settled) {
                toast('Nothing was charged.');
                resolve(false);
              }
            }
          }
        });

        rzp.on('payment.failed', function (e) {
          settled = true;
          var d = (e && e.error) || {};
          toast(d.description || 'That payment did not go through. Nothing was charged.', 5200);
          resolve(false);
        });

        rzp.open();
      });
    }).catch(function (e) {
      if (e && e.data && e.data.not_configured) {
        toast('Payments are not switched on yet. Nothing was charged.', 5000);
      } else {
        toast(e.message, 4500);
      }
      return false;
    });
  }

  /* Auto-renew. Checkout takes a subscription_id instead of an order_id
     and hands back a signature over payment|subscription — the other way
     round from a one-off, which is the sort of detail that costs a day
     if you assume. The app does not care; it forwards whatever it is
     given and the Worker decides. */
  function startAutopay() {
    var sub = null;

    return API.post('/app/pay/subscribe', {}).then(function (r) {
      if (r.already) {
        toast('Auto-renew is already on.');
        return false;
      }
      sub = r;
      return loadRazorpay().then(function (Razorpay) {
        return new Promise(function (resolve) {
          var settled = false;

          var rzp = new Razorpay({
            key: sub.key_id,
            subscription_id: sub.subscription_id,
            name: 'Comnito',
            description: sub.description,
            prefill: sub.prefill || {},
            theme: { color: '#17324F' },
            handler: function (res) {
              settled = true;
              toast('Confirming…');
              API.post('/app/pay/subscribe/confirm', {
                razorpay_subscription_id: res.razorpay_subscription_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature
              }).then(function () {
                toast('Auto-renew is on. Nothing to remember each month.', 4600);
                resolve(true);
              }).catch(function () {
                toast('Authorised. It is taking a moment to confirm — ' +
                      'refresh in a minute and it will be there.', 6000);
                resolve(false);
              });
            },
            modal: {
              ondismiss: function () {
                if (!settled) { toast('Auto-renew was not switched on.'); resolve(false); }
              }
            }
          });

          rzp.on('payment.failed', function (e) {
            settled = true;
            var d = (e && e.error) || {};
            toast(d.description || 'That did not go through. Nothing was charged.', 5200);
            resolve(false);
          });

          rzp.open();
        });
      });
    }).catch(function (e) {
      if (e && e.data && e.data.not_configured) {
        toast('Payments are not switched on yet.', 5000);
      } else {
        toast(e.message, 4500);
      }
      return false;
    });
  }

  /* The one place a 402 is turned into an offer. Every screen that hits
     a paywall calls this instead of writing its own sheet, so the words
     and the price come from the Worker rather than from whichever screen
     happened to hit the wall. */
  function offerUpgrade(err, onDone) {
    var up = (err && err.upgrade) || {};
    var price = up.price_inr ? money(up.price_inr) + (up.recurring ? '/month' : '') : '';

    return sheet({
      title: up.needs === 'base' ? 'Start your Comnito plan' : up.label || 'Upgrade',
      note: err.message,
      ok: price ? 'Pay ' + price : 'Continue',
      cancel: 'Not now'
    }).then(function (go) {
      if (!go) return false;
      return pay({
        intent: up.needs === 'base' ? 'plan' : 'grant',
        grant: up.needs,
        space_id: up.space_id || ''
      }).then(function (paid) {
        if (paid && onDone) onDone();
        return paid;
      });
    });
  }

  /* A screen with steps puts a function here; the header arrow calls it
     before falling through to the browser's own history. */
  var stepBack = null;

  var Screens = {};

  /* Hide the mobile-number path without removing it. The elements stay in
     the DOM because the rest of the login controller — including the leg that
     catches Google's #google_token= redirect — reads them. */
  function hideOtpLogin(root) {
    if (CFG.FEATURES.otpLogin) return;
    var scope = root || document;
    ['.eyebrow', '.field-label', '#phoneField', '#phoneHint', '#sendOtp',
     '.divider', '#step-otp'].forEach(function (sel) {
      $$(sel, scope).forEach(function (el) {
        el.hidden = true;
        el.style.display = 'none';
      });
    });
    var h = $('#ph-h', scope);
    if (h) h.textContent = 'Sign in to Comnito';
    var sub = $('.sub', scope);
    if (sub) sub.textContent =
      'Use your Google account. Signing in with a mobile number is coming soon.';
    var lede = $('.lede', scope);
    if (lede) lede.textContent =
      'Sign in to open your store, list your services, or just look around.';
  }

  /* The four things Shopify will not create a customer_profile without.
     Asked once, wherever the person happens to be, instead of behind a
     five-step wall before they have seen anything. */
  function askBasicDetails(prefill) {
    prefill = prefill || {};
    return sheet({
      title: 'Finish setting up your account',
      note: 'Your name and where you are. This is all we need — everything ' +
            'else can wait until you actually use it.',
      fields: [
        { name: 'name',  label: 'Your name or business name',
          placeholder: 'Lakshmana Tailor Shop', value: prefill.name },
        { name: 'city',  label: 'City', placeholder: 'Coimbatore',
          value: prefill.city },
        { name: 'area',  label: 'Your area', placeholder: 'RS Puram',
          value: prefill.area },
        { name: 'whatsapp', label: 'WhatsApp number', type: 'tel',
          inputmode: 'numeric', placeholder: '98765 43210',
          value: prefill.whatsapp || prefill.phone }
      ],
      ok: 'Save'
    }).then(function (r) {
      if (!r) return null;
      var num = String(r.values.whatsapp).replace(/\D/g, '');
      if (!/^[6-9]\d{9}$/.test(num)) {
        toast('That WhatsApp number does not look right');
        return askBasicDetails(r.values);
      }
      /* One number, written to both fields. Almost nobody has a different
         calling number, and Settings can split them later if they do. */
      return API.onboard({
        name: r.values.name, city: r.values.city, area: r.values.area,
        whatsapp: num, phone: num
      }).then(function () {
        toast('Saved');
        return r.values;
      }).catch(function (e) {
        toast(e.message, 5000);
        return askBasicDetails(r.values);
      });
    });
  }

  /* Centre-crop and downscale in the browser before uploading.

     Three reasons this is not optional. A phone camera photo is 3-8 MB and
     the upload cap is 5 MB, so half of them would simply fail. A 4:3 photo in
     a 3:1 cover slot gets its middle shown and the rest cut off, and people
     should see that before it is saved, not after. And uploading 8 MB over a
     patchy connection to store a 1200px banner is unkind to someone paying
     for data.

     This is a centre crop rather than a drag-to-position one — the important
     part of a shopfront photo is nearly always the middle, and a drag UI on a
     phone is a lot of surface for a small gain. Worth revisiting if people
     ask for it. */
  /* Three shapes, and the adjuster shows the one that matches. A listing
     photo was falling through to the cover's 1200 × 400 — so a person
     cropping a product photo was shown a letterbox and told it would be
     saved as a cover. Listing photos are square because that is what the
     grid they land in is. */
  var PIC_SPEC = {
    cover:   { w: 1200, h: 400,  label: '1200 × 400',  shape: 'wide',
               title: 'Position your cover' },
    profile: { w: 512,  h: 512,  label: '512 × 512',   shape: 'square',
               title: 'Position your logo' },
    photo:   { w: 1080, h: 1080, label: '1080 × 1080', shape: 'square',
               title: 'Position this photo' }
  };

  function cropToSpec(file, slot) {
    var spec = PIC_SPEC[slot] || PIC_SPEC.cover;
    return loadImage(file).then(function (img) {
      var canvas = document.createElement('canvas');
      canvas.width = spec.w;
      canvas.height = spec.h;
      var ctx = canvas.getContext('2d');
      var scale = Math.max(spec.w / img.width, spec.h / img.height);
      var dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, (spec.w - dw) / 2, (spec.h - dh) / 2, dw, dh);
      return canvasFile(canvas, slot);
    });
  }

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('That file is not an image we can read'));
      };
      img.src = url;
    });
  }

  function canvasFile(canvas, slot) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) return reject(new Error('Could not read that image'));
        resolve(new File([blob], slot + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.86);
    });
  }

  /* ---- adjust before uploading -------------------------------------
     Centre-cropping on its own throws away the part of the photo people
     actually care about: a shop board ends up half out of frame and a
     face lands under the edge of the round mask. This is the same crop
     maths, but the person drags and pinches until the frame holds what
     they meant, and the confirmed transform is what gets rendered.

     Everything is computed in OUTPUT pixels — the canvas is the exact
     size the file will be — so what is inside the frame is literally
     the file that gets uploaded. */
  function pickCrop(file, slot) {
    var spec = PIC_SPEC[slot] || PIC_SPEC.cover;

    return loadImage(file).then(function (img) {
      return new Promise(function (resolve) {
        var wrap = el('div', 'cmodal cropper');
        wrap.setAttribute('role', 'dialog');
        wrap.setAttribute('aria-modal', 'true');
        wrap.innerHTML =
          '<div class="cmodal-scrim" data-close="1"></div>' +
          '<div class="cmodal-card">' +
            '<div class="grip" aria-hidden="true"></div>' +
            '<h2 class="cmodal-t">' + esc(spec.title || 'Position this photo') + '</h2>' +
            '<p class="cmodal-n">Drag the photo to move it, and use the slider ' +
              'to zoom. Saved at ' + spec.label + '.</p>' +
            '<div class="cropstage' + (slot === 'profile' ? ' round' : '') + '">' +
              '<canvas class="cropcanvas"></canvas>' +
            '</div>' +
            '<label class="croplabel" for="cropzoom">Zoom</label>' +
            '<input class="cropzoom" id="cropzoom" type="range" ' +
              'min="100" max="300" value="100" step="1" aria-label="Zoom">' +
            '<div class="cmodal-actions">' +
              '<button type="button" class="btn btn-ghost" data-close="1">Cancel</button>' +
              '<button type="button" class="btn btn-primary" data-ok="1">Use photo</button>' +
            '</div>' +
          '</div>';

        var canvas = $('.cropcanvas', wrap);
        canvas.width = spec.w;
        canvas.height = spec.h;
        var ctx = canvas.getContext('2d');

        var fit = Math.max(spec.w / img.width, spec.h / img.height);
        var view = { scale: fit, x: 0, y: 0 };
        centre();

        function centre() {
          var dw = img.width * view.scale, dh = img.height * view.scale;
          view.x = (spec.w - dw) / 2;
          view.y = (spec.h - dh) / 2;
        }

        /* The image must never stop covering the frame, or the crop gets
           a band of blank canvas down one side. */
        function clamp() {
          var dw = img.width * view.scale, dh = img.height * view.scale;
          view.x = Math.min(0, Math.max(spec.w - dw, view.x));
          view.y = Math.min(0, Math.max(spec.h - dh, view.y));
        }

        function draw() {
          clamp();
          ctx.clearRect(0, 0, spec.w, spec.h);
          ctx.drawImage(img, view.x, view.y,
            img.width * view.scale, img.height * view.scale);
        }
        draw();

        var zoom = $('.cropzoom', wrap);
        zoom.addEventListener('input', function () {
          var next = fit * (Number(zoom.value) / 100);
          /* Zoom about the centre of the frame, not the top-left corner,
             so the thing you were looking at stays where you put it. */
          var cx = (spec.w / 2 - view.x) / view.scale;
          var cy = (spec.h / 2 - view.y) / view.scale;
          view.scale = next;
          view.x = spec.w / 2 - cx * next;
          view.y = spec.h / 2 - cy * next;
          draw();
        });

        /* Drag in CSS pixels, move in output pixels. */
        var dragging = false, lastX = 0, lastY = 0, ratio = 1;
        function ratioNow() {
          var r = canvas.getBoundingClientRect();
          return r.width ? spec.w / r.width : 1;
        }
        canvas.addEventListener('pointerdown', function (e) {
          dragging = true;
          ratio = ratioNow();
          lastX = e.clientX; lastY = e.clientY;
          try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
        });
        canvas.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          e.preventDefault();
          view.x += (e.clientX - lastX) * ratio;
          view.y += (e.clientY - lastY) * ratio;
          lastX = e.clientX; lastY = e.clientY;
          draw();
        });
        ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
          canvas.addEventListener(ev, function () { dragging = false; });
        });

        appRoot().appendChild(wrap);
        document.body.style.overflow = 'hidden';

        function close(result) {
          document.removeEventListener('keydown', onKey);
          document.body.style.overflow = '';
          wrap.remove();
          resolve(result);
        }
        function onKey(e) { if (e.key === 'Escape') close(null); }
        document.addEventListener('keydown', onKey);

        wrap.addEventListener('click', function (e) {
          if (e.target.closest('[data-close]')) return close(null);
          if (e.target.closest('[data-ok]')) {
            return canvasFile(canvas, slot).then(close, function () { close(null); });
          }
        });
      });
    });
  }

  /* Two pictures per space: a round profile mark and a wide cover.
     Both go through one hidden file input, so there is one code path for
     picking, uploading, failing and replacing. */
  function mountPictureEditor(host, space, onDone, before) {
    if (!host || !space || host.dataset.picsMounted) return;
    host.dataset.picsMounted = '1';

    var box = el('div', 'picedit');
    box.innerHTML =
      '<button class="picedit-cover" type="button" data-slot="cover">' +
        '<span class="picedit-hint">Add a cover photo</span>' +
      '</button>' +
      '<button class="picedit-avatar" type="button" data-slot="profile">' +
        '<span class="picedit-hint">Logo</span>' +
      '</button>' +
      '<p class="picedit-spec">Tap either one to add a photo — you position ' +
        'it yourself, and it is saved at the right size.</p>';

    var file = el('input');
    file.type = 'file';
    file.accept = 'image/*';
    file.hidden = true;
    box.appendChild(file);

    function paint(s) {
      var cover = $('.picedit-cover', box), av = $('.picedit-avatar', box);
      if (s.cover_image_url) {
        cover.style.backgroundImage = 'url("' + s.cover_image_url + '")';
        cover.classList.add('has');
      } else {
        cover.style.backgroundImage = '';
        cover.classList.remove('has');
      }
      var spec = $('.picedit-spec', box);
      if (spec) spec.hidden = !!(s.cover_image_url && s.profile_image_url);
      if (s.profile_image_url) {
        av.style.backgroundImage = 'url("' + s.profile_image_url + '")';
        av.classList.add('has');
      } else {
        av.style.backgroundImage = '';
        av.classList.remove('has');
        $('.picedit-hint', av).textContent =
          (s.title || 'C').trim().charAt(0).toUpperCase();
      }
    }
    paint(space);

    var pending = '';
    box.addEventListener('click', function (e) {
      var b = e.target.closest('[data-slot]');
      if (!b) return;
      pending = b.dataset.slot;
      file.value = '';
      file.click();
    });

    file.addEventListener('change', function () {
      var f = file.files && file.files[0];
      if (!f || !pending) return;
      var slot = pending;
      pickCrop(f, slot)
        .then(function (ready) {
          /* Cancelled in the adjuster — not an error, and not an upload. */
          if (!ready) return null;
          toast(slot === 'cover' ? 'Uploading cover…' : 'Uploading logo…');
          return API.spacePicture(ready, slot, space.id);
        })
        .then(function (r) {
          if (!r) return;
          paint(r.settings || {});
          toast(slot === 'cover' ? 'Cover updated' : 'Logo updated');
          if (onDone) onDone(r.settings);
        })
        .catch(function (err) { toast(err.message, 5000); });
    });

    host.insertBefore(box, before || host.firstChild);
  }

  /* How finished the account is, as a ring with the next step named.
     A bare percentage is a scold; a percentage that says what to do next and
     takes you there is a nudge. */
  var COMPLETE_WHERE = {
    details: function () { askBasicDetails(Session.me && Session.me.member); },
    space:   function () { location.href = CFG.PAGES.space; },
    listing: function () { location.href = CFG.PAGES.addListing; },
    kyc:     function () { location.href = CFG.PAGES.settings + '#verify'; }
  };

  function mountCompleteness(host, c) {
    if (!host || !c || c.percent >= 100) return;
    if ($('.ringcard', host.parentNode || host)) return;

    var r = 26, circ = 2 * Math.PI * r;
    var card = el('div', 'ringcard');
    card.innerHTML =
      '<div class="ring" role="img" aria-label="Profile ' + c.percent + '% complete">' +
        '<svg width="64" height="64" viewBox="0 0 64 64">' +
          '<circle class="ring-bg" cx="32" cy="32" r="' + r + '"></circle>' +
          '<circle class="ring-fg" cx="32" cy="32" r="' + r + '" ' +
            'stroke-dasharray="' + circ.toFixed(1) + '" ' +
            'stroke-dashoffset="' + (circ * (1 - c.percent / 100)).toFixed(1) + '">' +
          '</circle>' +
        '</svg>' +
        '<span class="ring-n">' + c.percent + '<i>%</i></span>' +
      '</div>' +
      '<div class="ringcard-tx">' +
        '<b>Your page is ' + c.percent + '% set up</b>' +
        '<span>' + (c.next ? 'Next: ' + esc(c.next.label) : 'Everything is done') +
        '</span>' +
      '</div>' +
      (c.next ? '<button class="ringcard-go" type="button">Add</button>' : '');

    var go = $('.ringcard-go', card);
    if (go && c.next) {
      go.addEventListener('click', function () {
        (COMPLETE_WHERE[c.next.where] || COMPLETE_WHERE.details)();
      });
    }
    host.parentNode.insertBefore(card, host);
  }

  /* ---------------------------------------------------------------- LOGIN */
  Screens.login = function (root) {
    var phone = $('#phone'), field = $('#phoneField'), hint = $('#phoneHint'),
        sendBtn = $('#sendOtp'),
        stepPhone = $('#step-phone'), stepOtp = $('#step-otp'), stepDone = $('#step-done'),
        otpWrap = $('#otpBoxes'),
        otps = otpWrap ? $$('input', otpWrap) : [],
        verifyBtn = $('#verify'), timerEl = $('#timer');

    hideOtpLogin(root);

    if (!phone) return;

    /* Already signed in? Skip straight through. */
    if (Session.token) {
      API.me().then(function (r) {
        Session.me = r;
        location.href = r.needs_onboarding ? CFG.PAGES.onboarding
                                           : (qs('return') || CFG.PAGES.home);
      }).catch(function () { Session.clear(); });
    }

    function show(step) {
      [stepPhone, stepOtp, stepDone].forEach(function (s) {
        if (s) s.hidden = (s !== step);
      });
    }

    function fmt(v) {
      v = v.replace(/\D/g, '').slice(0, 10);
      return v.length > 5 ? v.slice(0, 5) + ' ' + v.slice(5) : v;
    }

    phone.addEventListener('input', function () {
      var raw = phone.value.replace(/\D/g, '').slice(0, 10);
      phone.value = fmt(phone.value);
      var valid = /^[6-9]\d{9}$/.test(raw);
      sendBtn.disabled = !valid;
      if (field) field.classList.remove('invalid');
      if (!hint) return;
      if (raw.length === 10 && !valid) {
        hint.textContent = 'Indian mobile numbers start with 6, 7, 8 or 9.';
        hint.classList.add('error');
        if (field) field.classList.add('invalid');
      } else {
        hint.textContent = 'Indian mobile numbers only, for now.';
        hint.classList.remove('error');
      }
    });

    var countdown;
    function startTimer() {
      var left = 30;
      clearTimeout(countdown);
      (function tick() {
        if (!timerEl) return;
        if (left <= 0) {
          timerEl.innerHTML = '<button class="linkbtn" id="resend">Resend code</button>';
          var r = $('#resend');
          if (r) r.addEventListener('click', function () { doSend(true); });
          return;
        }
        timerEl.textContent = 'Resend code in 0:' + (left < 10 ? '0' : '') + left;
        left--;
        countdown = setTimeout(tick, 1000);
      })();
    }

    function raw() { return phone.value.replace(/\D/g, '').slice(0, 10); }

    function doSend(again) {
      sendBtn.disabled = true;
      var prev = sendBtn.textContent;
      sendBtn.textContent = 'Sending…';
      API.sendOtp(raw())
        .then(function () {
          var m = $('#maskedNum');
          if (m) m.textContent = '+91 ' + phone.value;
          show(stepOtp);
          startTimer();
          if (otps[0]) setTimeout(function () { otps[0].focus(); }, 120);
          toast(again ? 'Code sent again' : 'Code sent on WhatsApp to +91 ' + phone.value);
        })
        .catch(function (e) { toast(e.message || 'Could not send the code'); })
        .then(function () { sendBtn.disabled = false; sendBtn.textContent = prev; });
    }

    sendBtn.addEventListener('click', function () { doSend(false); });

    function otpValue() { return otps.map(function (i) { return i.value; }).join(''); }
    function sync() {
      otps.forEach(function (i) { i.classList.toggle('filled', i.value !== ''); });
      if (verifyBtn) verifyBtn.disabled = otpValue().length !== 6;
    }

    otps.forEach(function (box, i) {
      box.addEventListener('input', function () {
        box.value = box.value.replace(/\D/g, '').slice(0, 1);
        if (box.value && i < otps.length - 1) otps[i + 1].focus();
        sync();
        if (otpValue().length === 6) doVerify();
      });
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Backspace' && !box.value && i > 0) {
          otps[i - 1].focus(); otps[i - 1].value = ''; sync();
        }
        if (e.key === 'ArrowLeft' && i > 0) otps[i - 1].focus();
        if (e.key === 'ArrowRight' && i < otps.length - 1) otps[i + 1].focus();
      });
      box.addEventListener('paste', function (e) {
        e.preventDefault();
        var d = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6).split('');
        d.forEach(function (ch, k) { if (otps[k]) otps[k].value = ch; });
        sync();
        (otps[Math.min(d.length, 5)] || otps[5]).focus();
        if (otpValue().length === 6) doVerify();
      });
    });

    var verifying = false;
    function doVerify() {
      if (verifying) return;
      if (otpValue().length !== 6) {
        otpWrap.classList.add('shake');
        setTimeout(function () { otpWrap.classList.remove('shake'); }, 420);
        return;
      }
      verifying = true;
      if (verifyBtn) { verifyBtn.disabled = true; verifyBtn.textContent = 'Verifying…'; }

      API.verifyOtp(raw(), otpValue())
        .then(function (r) {
          clearTimeout(countdown);
          Session.token = r.token;
          Session.me = r;
          show(stepDone);
          setTimeout(function () {
            location.href = r.needs_onboarding
              ? CFG.PAGES.onboarding
              : (qs('return') || CFG.PAGES.home);
          }, 700);
        })
        .catch(function (e) {
          verifying = false;
          if (verifyBtn) { verifyBtn.disabled = false; verifyBtn.textContent = 'Verify'; }
          otpWrap.classList.add('shake');
          setTimeout(function () { otpWrap.classList.remove('shake'); }, 420);
          toast(e.message || 'That code did not match');
        });
    }

    if (verifyBtn) verifyBtn.addEventListener('click', doVerify);

    function backToPhone() {
      clearTimeout(countdown);
      otps.forEach(function (i) { i.value = ''; });
      sync(); show(stepPhone); phone.focus();
    }
    var edit = $('#editNum'), b2p = $('#backToPhone');
    if (edit) edit.addEventListener('click', backToPhone);
    if (b2p) b2p.addEventListener('click', backToPhone);

    /* bound directly and again by delegation, so a theme that re-renders
       or clones the button cannot silently break sign-in */
    var g = hook('googleBtn');
    if (g) g.addEventListener('click', googleSignIn);

    var guest = $('#guestBtn');
    if (guest) guest.addEventListener('click', function () {
      location.href = CFG.PAGES.nearby;
    });

    /* Google comes back with a hash. Read it before anything else can
       navigate away. */
    if (location.hash.length > 1) {
      var h = new URLSearchParams(location.hash.slice(1));
      var t = h.get('google_token');

      if (t) {
        Session.token = t;
        try { sessionStorage.removeItem('comnito_401'); } catch (e) {}
        history.replaceState(null, '', location.pathname + location.search);
        /* A new member should land in onboarding, not on an empty home. Ask
           the Worker rather than guessing from the hash. */
        /* Straight to the dashboard, whether or not the account is finished.
           Setup used to be a wall in front of a product nobody had seen yet,
           and every failure this week came from doing too much at sign-in.
           The dashboard asks for what is missing, in place. */
        API.me()
          .then(function (r) {
            Session.me = r;
            location.href = qs('return') || CFG.PAGES.home;
          })
          .catch(function (e) {
            Session.clear();
            toast(e.message || 'Signed in, but the account could not be loaded.', 6000);
          });
      } else if (h.get('registration_token') || h.get('registration_required')) {
        /* The Worker still has the v1 flow, which sends a new Google user
           back to fill in the directory registration form before they have
           an account at all. The v2 app has no such step, so this hash used
           to be ignored and sign-in looked like it simply did nothing.
           Deploy the current Worker and this branch stops being reachable. */
        history.replaceState(null, '', location.pathname + location.search);
        toast('This Comnito Worker is still on the old sign-up flow. ' +
              'Deploy the current Worker and sign in again.', 7000);
        console.error('[Comnito] Worker returned registration_token — ' +
          'the deployed Worker predates the v2 Google sign-up path.');
      }
    }
  };

  function googleSignIn(e) {
    if (e) e.preventDefault();
    var url = CFG.WORKER + '/auth/google';
    console.log('[Comnito] Google sign-in ->', url);
    location.href = url;
  }

  /* Delegated: works even if the login controller never ran, which is the
     usual reason "nothing happens" when the page markup lost data-screen. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest('#googleBtn, [data-comnito="googleBtn"], [data-google-signin]');
    if (b) googleSignIn(e);
  });

  /* ----------------------------------------------------------- ONBOARDING */
  Screens.onboarding = function (root) {
    if (!Session.requireLogin()) return;

    /* The wizard.
     *
     * The markup ships five <section data-ob="…"> steps with four of them
     * marked hidden, and the prototype's own <script> used to walk them. That
     * script is stripped for Shopify, and nothing here replaced it — so only
     * step 1 ever showed. Every field the account needs lives in step 2, so
     * they were in the file and never on screen, and Continue submitted an
     * empty form.
     */
    var steps = $$('[data-ob]', root);
    var dots  = $$('.dots i', root);
    var next  = hook('finishOnboarding');
    var idx   = 0;
    var LAST  = steps.length - 1;          // the "done" screen
    var created = null;                    // the space, once it exists

    var state = { name: '', city: '', area: '', pincode: '', phone: '',
                  lat: '', lng: '', interests: [], space_type: 'store' };

    document.addEventListener('comnito:change', function (e) {
      var d = e.detail;
      if (d.name === 'interests') state.interests = [].concat(d.value);
    });

    /* ---- step 1: what brings you here ------------------------------- */
    var TYPE = { serv: 'services', store: 'store', pers: 'personal' };
    $$('.scard[data-sp]', root).forEach(function (card) {
      card.addEventListener('click', function () {
        $$('.scard[data-sp]', root).forEach(function (x) {
          x.setAttribute('aria-pressed', 'false');
        });
        card.setAttribute('aria-pressed', 'true');
        state.space_type = TYPE[card.dataset.sp] || 'store';
        if (next) next.disabled = false;
        paintCategories();
        paintPreview();
      });
    });
    var look = $('#justLook', root);
    if (look) look.addEventListener('click', function () {
      location.href = CFG.PAGES.nearby;
    });

    /* ---- step 2: the details the account cannot exist without -------- */
    function readFields() {
      ['name', 'city', 'area', 'pincode', 'phone'].forEach(function (k) {
        var f = hook(k);
        if (f) state[k] = String(f.value || '').trim();
      });
      /* The field is labelled WhatsApp and that is the required one; the
         same number goes in as the calling number unless it is changed
         later in Settings. */
      state.whatsapp = state.phone;
    }

    function missingOnStep2() {
      readFields();
      var need = [
        ['name',  'What should we call it?'],
        ['area',  'Which area? People nearby find you by it'],
        ['city',  'Which city are you in?'],
        ['phone', 'Your WhatsApp number — this is how customers reach you']
      ].filter(function (pair) { return !state[pair[0]]; })[0];
      if (need) {
        var f = hook(need[0]);
        if (f && f.focus) { try { f.focus(); } catch (e) {} }
        return need[1];
      }
      if (!/^[6-9]\d{9}$/.test(state.phone.replace(/\D/g, ''))) {
        return 'That WhatsApp number does not look right';
      }
      return null;
    }

    /* the little preview card in the header */
    var live = $('#live', root), lvNm = $('#lvNm', root),
        lvUrl = $('#lvUrl', root), lvAv = $('#lvAv', root);
    function paintPreview() {
      var name = (hook('name') || {}).value || '';
      if (!live) return;
      live.hidden = !name;
      if (!name) return;
      if (lvNm) lvNm.textContent = name;
      if (lvAv) lvAv.textContent = name.trim().charAt(0).toUpperCase() || '?';
      if (lvUrl) {
        lvUrl.innerHTML = '<em>comnito.com/' + esc(state.space_type) + '/</em>' +
          esc(slugify(name));
      }
    }
    var nameField = hook('name');
    if (nameField) nameField.addEventListener('input', paintPreview);

    /* ---- prefill whatever the Worker already knows ------------------- */
    API.me().then(function (r) {
      var m = r.member || {};
      [['name', m.name], ['city', m.city], ['area', m.area],
       ['phone', m.whatsapp || m.phone]].forEach(function (pair) {
        var f = hook(pair[0]);
        if (f && !f.value && pair[1]) f.value = pair[1];
      });
      paintPreview();
    }).catch(quiet('your account'));

    /* ---- the category grid, from the live taxonomy ------------------- */
    var cats = hook('interests');
    function paintCategories() {
      if (!cats) return;
      API.taxonomy().then(function (r) {
        /* Only the categories for the space they picked. This used to merge
           business, services and personal into one list, so choosing "I sell
           things" offered tailoring and sports clubs alongside retail. */
        var tax = r.taxonomy || {};
        var group = tax[CFG.MAIN_TYPE[state.space_type]] || tax.business || {};
        var names = (group.categories || []).map(function (c) { return c.name; });
        cats.classList.add('chipset');
        cats.dataset.name = 'interests';
        cats.dataset.label = 'Interests';
        cats.innerHTML = names.map(function (n) {
          return '<button class="chip" data-value="' + esc(n) + '">' +
            symbolFor(n) + ' ' + esc(n) + '</button>';
        }).join('');
        bindControls(cats.parentNode);
      }).catch(quiet('onboarding categories'));
    }
    paintCategories();

    /* ---- location ---------------------------------------------------- */
    var locBtn = hook('useLocation');
    if (locBtn) locBtn.addEventListener('click', function () {
      if (!navigator.geolocation) return toast('Location is not available on this device');
      locBtn.disabled = true;
      navigator.geolocation.getCurrentPosition(function (pos) {
        state.lat = pos.coords.latitude;
        state.lng = pos.coords.longitude;
        fetch(CFG.WORKER + '/reverse-geocode?lat=' + state.lat + '&lng=' + state.lng)
          .then(function (r) { return r.json(); })
          .then(function (r) {
            var L = (r && r.location) || {};
            ['city', 'area', 'pincode'].forEach(function (k) {
              var f = hook(k);
              if (f && L[k]) { f.value = L[k]; state[k] = L[k]; }
            });
            toast(L.area ? (L.area + ', ' + L.city) : 'Location set');
          })
          .catch(function () { toast('Location set'); })
          .then(function () { locBtn.disabled = false; });
      }, function () {
        locBtn.disabled = false;
        toast('Could not read your location — type your area instead');
      }, { enableHighAccuracy: true, timeout: 10000 });
    });

    /* ---- moving between steps ---------------------------------------- */
    function show(i) {
      idx = Math.max(0, Math.min(i, LAST));
      steps.forEach(function (sec, k) { sec.hidden = k !== idx; });
      dots.forEach(function (d, k) {
        d.classList.toggle('on', k <= Math.min(idx, dots.length - 1));
      });
      if (next) {
        next.hidden = idx === LAST;
        next.disabled = idx === 0 && !$('.scard[aria-pressed="true"]', root);
        next.textContent = idx === LAST - 2 ? 'Finish' : 'Continue';
      }
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
    }

    var skipListing = $('#skipL', root);
    if (skipListing) skipListing.addEventListener('click', function () { finish(); });

    if (next) next.addEventListener('click', function () {
      if (idx === 0 && !$('.scard[aria-pressed="true"]', root)) {
        return toast('Pick the one that fits you best');
      }
      if (idx === 1) {
        var problem = missingOnStep2();
        if (problem) return toast(problem);
      }
      if (idx === 2 && !state.interests.length) {
        return toast('Pick at least one — this is how people find you');
      }
      /* Step 4 is a toy — two fields and photo slots that were never wired.
         The real Add listing screen has the title, description, tags, prices,
         variants and working photo upload, so finish here and send them
         there instead of collecting a worse version of the same thing. */
      if (idx === LAST - 2) return finish();
      show(idx + 1);
    });

    /* ---- the one write that matters ---------------------------------- */
    function finish() {
      readFields();
      var problem = missingOnStep2();
      if (problem) { show(1); return toast(problem); }

      if (next) { next.disabled = true; next.textContent = 'Setting up…'; }

      /* 1. the account. For a Google sign-up this is where it is created —
            the Worker has no profile for them until now. */
      API.onboard(state)
        .then(function () {
          /* 2. their first space, so "your page is ready" is true. */
          return API.createSpace({
            type: state.space_type,
            title: state.name,
            description: state.name + ' — ' + (state.interests[0] || 'local business') +
                         ' in ' + (state.area || state.city),
            categories: state.interests.slice(0, 1),
            city: state.city, area: state.area,
            whatsapp: state.phone, phone: state.phone,
            pincode: state.pincode, lat: state.lat, lng: state.lng
          });
        })
        .then(function (r) {
          created = r.space;
          var link = hook('spaceLink');
          if (link && created) {
            link.innerHTML = '<em>comnito.com/' + esc(created.type) + '/</em>' +
              esc(created.handle);
          }
          return null;
        })
        .then(function () {
          show(LAST);
          /* The last screen offers the real thing rather than a stub form. */
          var addFirst = $('#cpy', root);
          if (addFirst && addFirst.parentNode) {
            var go = el('button', 'btn btn-primary wide');
            go.textContent = 'Add your first listing';
            go.style.marginTop = '16px';
            go.addEventListener('click', function () {
              location.href = CFG.PAGES.addListing +
                '?space=' + encodeURIComponent(created.id);
            });
            var host = $('.donewrap', root);
            if (host && host.parentNode) host.parentNode.appendChild(go);
          }
        })
        .catch(function (e) {
          if (next) { next.disabled = false; next.textContent = 'Continue'; }
          toast(e.message || 'Could not finish setting up', 5000);
        });
    }

    var copy = $('#cpy', root);
    if (copy) copy.addEventListener('click', function () {
      var link = hook('spaceLink');
      var text = link ? link.textContent.trim() : '';
      if (!text) return;
      try {
        navigator.clipboard.writeText('https://' + text);
        toast('Link copied');
      } catch (e) { toast(text, 4000); }
    });

    var skip = hook('skipOnboarding');
    if (skip) skip.addEventListener('click', function () {
      location.href = created ? CFG.PAGES.space : CFG.PAGES.home;
    });

    show(0);
  };


  Screens.dashboard = function () {
    if (!Session.requireLogin()) return;

    API.me().then(function (r) {
      Session.me = r;
      var m = r.member || {};

      /* Two things can be unfinished, and each has one card that fixes it.
         Nothing blocks the screen: they can look around first, which is the
         whole point of showing the dashboard rather than a setup wizard. */
      if (r.pending || (r.missing || []).length) {
        showSetupCard(
          'Finish setting up your account',
          'Your name, city, area and a mobile number. Takes a moment, and ' +
          'nothing else works until it is done.',
          'Add my details',
          function () {
            askBasicDetails(m).then(function (done) {
              if (done) location.reload();
            });
          }
        );
      } else if (!(r.spaces || []).length) {
        showSetupCard(
          'Create your first space',
          'A Store to sell, Services to take bookings, or a Personal page to ' +
          'meet people. You can add the others later.',
          'Create a space',
          function () { location.href = CFG.PAGES.space; }
        );
      }

      if (!r.pending) loadRequests();
      if (r.completeness) mountCompleteness(dashTop(), r.completeness);

      setText($('.greet .who'), m.name || 'there');
      var hi = $('.greet .hi');
      if (hi) {
        var h = new Date().getHours();
        hi.textContent = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
      }
      var loc = $('#loc');
      if (loc && m.area) loc.textContent = m.area + (m.city ? ', ' + m.city : '');

      renderSpaces(r.spaces || []);
    }).catch(function (e) { toast(e.message); });

    /* Deliberately inside the me() result: a pending account has no member
       record yet, so every other route answers 401, and an unguarded call
       here would bounce them back to login. */
    function loadRequests() {
      API.requests('owner', 'open').then(function (r) {
        var n = (r.requests || []).length;
        setNavBadge('space', n);
        var pill = hook('reqCount');
        if (pill) { pill.textContent = n; pill.hidden = n === 0; }
        renderRequests(r.requests || []);
      }).catch(quiet('your requests'));
    }

    /* Genuinely at the top. Mounting these before the space strip put them
       three screens down, past Quick actions and This week — which is no
       use for a nudge about something unfinished. */
    /* The first block inside the dashboard's own body — above Quick actions,
       not three screens down next to the space strip. .wrap exists on this
       page but is empty, which is how the first attempt at this still landed
       at 961px. */
    function dashTop() {
      var body = $('.feedbody') || $('main') || $('.wrap');
      if (body && body.firstElementChild) return body.firstElementChild;
      return hook('spaceStrip') || document.body.firstElementChild;
    }

    function showSetupCard(title, note, cta, onClick) {
      var host = dashTop();
      var card = el('div', 'setupcard');
      card.innerHTML =
        '<h2 class="setupcard-t">' + esc(title) + '</h2>' +
        '<p class="setupcard-n">' + esc(note) + '</p>' +
        '<button class="btn btn-primary setupcard-b">' + esc(cta) + '</button>';
      card.querySelector('.setupcard-b').addEventListener('click', onClick);
      host.parentNode.insertBefore(card, host);
    }

    function renderSpaces(spaces) {
      var mount = hook('spaceStrip');
      if (!mount) return;
      if (!spaces.length) {
        mount.innerHTML = empty('No space yet',
          'Create your Store, Services or Personal space to start.');
        return;
      }
      mount.innerHTML = spaces.map(function (s) {
        return '<a class="spacetile tone-' + spaceTone(s.type) + '" href="' +
          CFG.PAGES.space + '?space=' + encodeURIComponent(s.id) + '">' +
          '<span class="spacetile-t">' + esc(s.title || s.type) + '</span>' +
          '<span class="spacetile-m">' + (s.listing_count || 0) + ' listings · ' +
          (s.published ? 'Live' : 'Draft') + '</span></a>';
      }).join('');
    }

    function renderRequests(list) {
      var mount = hook('recentRequests');
      if (!mount) return;
      if (!list.length) {
        mount.innerHTML = empty('No open requests', 'New enquiries land here.');
        return;
      }
      mount.innerHTML = list.slice(0, 4).map(function (d) {
        return '<a class="reqrow" href="' + CFG.PAGES.requests + '#' + esc(d.id) + '">' +
          '<span class="reqrow-t">' + esc(d.from_name || 'Someone') + '</span>' +
          '<span class="reqrow-s">' + esc(d.kind || 'enquiry') + ' · ' + ago(d.created_at) + '</span>' +
          '</a>';
      }).join('');
    }
  };

  /* --------------------------------------------------------------- NEARBY */
  Screens.nearby = function () {
    var mount = hook('nearbyList');
    var state = { lat: '', lng: '', radius: 25, space: '', q: '' };

    /* Nearby is where most people first see what Comnito is — a list of shops
       near them — and until now there was nothing on it inviting them to
       become one, or to pass it on. Both go under the results, where someone
       lands after scrolling through what is already there. */
    function mountNearbyCta() {
      if (!mount || !mount.parentNode || $('.nearcta', mount.parentNode)) return;

      var box = el('div', 'nearcta');
      box.innerHTML =
        '<div class="nearcta-row">' +
          '<div class="nearcta-tx">' +
            '<b>Are you here too?</b>' +
            '<span>Add your store, your services or your own page — free, and ' +
            'people searching this area will find you.</span>' +
          '</div>' +
          '<button class="btn btn-primary nearcta-go" type="button">Add my page</button>' +
        '</div>' +
        '<button class="nearcta-share" type="button">' +
          '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" ' +
          'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" ' +
          'stroke-linejoin="round"><circle cx="18" cy="5" r="3"/>' +
          '<circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
          '<path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>' +
          'Share this with your circle</button>';

      box.querySelector('.nearcta-go').addEventListener('click', function () {
        location.href = Session.token ? CFG.PAGES.space : CFG.PAGES.login;
      });

      box.querySelector('.nearcta-share').addEventListener('click', function () {
        /* Share the Nearby view for the area they are looking at, so whoever
           opens it lands on the same shops rather than a generic page. */
        var url = location.origin + CFG.PAGES.nearby +
          (state.radius ? '?radius=' + encodeURIComponent(state.radius) : '');
        var text = 'Shops, services and people near us on Comnito';
        if (navigator.share) {
          navigator.share({ title: 'Comnito · Near You', text: text, url: url })
            .catch(quiet('the share sheet'));
          return;
        }
        try {
          navigator.clipboard.writeText(url);
          toast('Link copied — paste it into your group');
        } catch (e) { toast(url, 5000); }
      });

      mount.parentNode.insertBefore(box, mount.nextSibling);
    }

    document.addEventListener('comnito:change', function (e) {
      var d = e.detail;
      if (d.name === 'radius') { state.radius = d.value; load(); }
      if (d.name === 'space')  { state.space = d.value === 'All' ? '' : d.value; load(); }
    });

    mountNearbyCta();

    var filter = hook('spaceFilter');
    if (filter) filter.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      $$('button', filter).forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
      b.setAttribute('aria-pressed', 'true');
      var v = (b.dataset.value || b.textContent).trim().toLowerCase();
      state.space = (v === 'all' || v === 'everything') ? '' : v;
      load();
    });

    var q = hook('searchInput') || $('#q');
    if (q) q.addEventListener('input', debounce(function () {
      state.q = q.value.trim(); load();
    }, 350));

    function locate() {
      if (!navigator.geolocation) return load();
      navigator.geolocation.getCurrentPosition(function (p) {
        state.lat = p.coords.latitude; state.lng = p.coords.longitude; load();
      }, function () {
        toast('Turn on location to see what is around you');
        load();
      }, { timeout: 8000 });
    }

    function load() {
      if (!mount) return;
      mount.setAttribute('aria-busy', 'true');
      API.nearby({
        lat: state.lat, lng: state.lng, radius: state.radius,
        space: state.space, search: state.q
      })
        .then(function (r) {
          var list = r.listings || [];
          mount.removeAttribute('aria-busy');
          if (!list.length) {
            mount.innerHTML = empty('Nothing within ' + state.radius + ' km',
              'Try a wider radius, or a different space.');
            return;
          }
          mount.innerHTML = list.map(function (s) {
            /* A cover turns a row of monogram tiles into a row of
               shopfronts, which is most of what makes Nearby worth opening.
               Without one the card falls back to the initial, as before. */
            var initial = esc((s.title || s.business_name || 'C').trim()
                              .charAt(0).toUpperCase());
            var cover = s.cover_image_url
              ? '<span class="nearcard-cover" style="background-image:url(&quot;' +
                esc(s.cover_image_url) + '&quot;)"></span>'
              : '';
            var av = s.profile_image_url
              ? '<span class="nearcard-av has" style="background-image:url(&quot;' +
                esc(s.profile_image_url) + '&quot;)"></span>'
              : '<span class="nearcard-av">' + initial + '</span>';

            return '<a class="nearcard tone-' + spaceTone(s.type) +
              (cover ? ' has-cover' : '') + '" href="' + publicUrl(s) + '">' +
              cover + av +
              '<h3 class="nearcard-t">' + esc(s.title || s.business_name) + '</h3>' +
              '<p class="nearcard-c">' + esc(s.category || '') + '</p>' +
              '<p class="nearcard-d">' +
                (s.distance_km != null ? s.distance_km + ' km' : '') +
                (s.open_today ? ' · Open now' : '') +
              '</p></a>';
          }).join('');
        })
        .catch(function (e) {
          mount.removeAttribute('aria-busy');
          mount.innerHTML = empty('Could not load', e.message);
        });
    }

    locate();
  };

  /* The shared link. Shopify serves a page at exactly one URL, so the
     shop still has to be named somewhere — but it does not have to be
     named in a query string. A fragment reads as part of the address
     rather than as machinery bolted onto it:

         comnito.com/pages/my-personal#lakshmana-tailor

     ?handle= keeps working, because links sent before this change must
     not stop working. */
  /* The link people share. It goes to the page the WORKER renders, not
     the one the app fills in — a Shopify page is empty until script
     runs, so every link Comnito ever produced previewed in WhatsApp as
     a grey rectangle with a URL under it. Nobody taps that.

     The old /pages/... links still work; they are just not what gets
     handed out any more. */
  function publicUrl(s) {
    return CFG.SHARE_BASE + '/p/' + encodeURIComponent(s.handle || s.id);
  }

  /* Where the app's own in-page version lives, for the times we want to
     stay inside the app rather than send somebody out to the shared
     page — a preview of your own page, say. */
  function publicAppUrl(s) {
    var t = spaceTone(s.type);
    var page = t === 'services' ? CFG.PAGES.services
             : t === 'personal' ? CFG.PAGES.personal
             : CFG.PAGES.store;
    return page + '#' + encodeURIComponent(s.handle || s.id);
  }

  /* Which shop a public page is showing. The fragment first, because
     that is what we hand out now; the query string after it, because
     that is what we handed out before. */
  function publicHandle() {
    var hash = String(location.hash || '').replace(/^#/, '');
    if (hash) { try { return decodeURIComponent(hash); } catch (e) { return hash; } }
    return qs('handle');
  }

  /* ---------------------------------------------------------------- FEEDS */
  Screens.feeds = function () {
    var mount = hook('feedList');
    var postBtn = hook('postBtn'), textArea = hook('postText');

    function load() {
      if (!mount) return;
      API.feed().then(function (r) {
        var posts = r.posts || [];
        if (!posts.length) {
          mount.innerHTML = empty('Nothing here yet',
            'Follow a few stores and people, or post something.');
          return;
        }
        mount.innerHTML = posts.map(postCard).join('');
      }).catch(function (e) {
        mount.innerHTML = empty('Could not load feed', e.message);
      });
    }

    function postCard(p) {
      return '<article class="post" data-id="' + esc(p.id) + '">' +
        '<header class="post-h">' +
          '<span class="post-who">' + esc(p.author_name || 'Comnito member') + '</span>' +
          '<span class="post-when">' + ago(p.created_at) + '</span>' +
        '</header>' +
        '<p class="post-b">' + esc(p.text || '') + '</p>' +
        (p.image_url ? '<img class="post-img" src="' + esc(p.image_url) + '" alt="" loading="lazy">' : '') +
        '<footer class="post-f">' +
          '<button class="post-a" data-act="like">' +
            (p.liked ? '♥ ' : '♡ ') + (p.likes || 0) + '</button>' +
          '<button class="post-a" data-act="follow">' +
            (p.following ? 'Following' : 'Follow') + '</button>' +
          '<button class="post-a" data-act="report">Report</button>' +
        '</footer></article>';
    }

    if (mount) mount.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b) return;
      var card = b.closest('.post'), id = card && card.dataset.id;
      if (!id) return;
      if (!Session.token) return Session.requireLogin();

      if (b.dataset.act === 'like') {
        API.like(id).then(function (r) {
          b.textContent = (r.liked ? '♥ ' : '♡ ') + (r.likes || 0);
        }).catch(function (e) { toast(e.message); });
      }
      if (b.dataset.act === 'follow') {
        var on = b.textContent.trim() !== 'Following';
        API.follow(card.dataset.author || id, on).then(function () {
          b.textContent = on ? 'Following' : 'Follow';
          toast(on ? 'Following' : 'Unfollowed');
        }).catch(function (e) { toast(e.message); });
      }
      if (b.dataset.act === 'report') {
        API.report({ kind: 'post', target_id: id })
          .then(function () { toast('Reported. Our team will look at it.'); })
          .catch(function (e) { toast(e.message); });
      }
    });

    /* Posting is free and always has been — no entitlement check touches it.
       But everything beside it is paid, so people assume. Saying so once,
       under the composer, is the whole fix. */
    if (textArea && textArea.parentNode && !$('.freenote', textArea.parentNode)) {
      var free = el('p', 'freenote');
      free.textContent = 'Posting to Feeds is free, always. No listing, ' +
        'no publish pack, nothing to pay.';
      textArea.parentNode.appendChild(free);
    }

    if (postBtn && textArea) postBtn.addEventListener('click', function () {
      var text = textArea.value.trim();
      if (!text) return toast('Write something first');
      if (!Session.requireLogin()) return;
      postBtn.disabled = true;
      API.createPost({ text: text })
        .then(function () { textArea.value = ''; postBtn.disabled = false; load(); toast('Posted'); })
        .catch(function (e) { postBtn.disabled = false; toast(e.message); });
    });

    load();
  };

  /* ------------------------------------------------------------- MY SPACE */
  Screens['my-space'] = function (root) {
    if (!Session.requireLogin()) return;

    var current = qs('space') || '';
    var activeSpace = null;

    API.spaces().then(function (r) {
      var spaces = r.spaces || [];
      if (!current && spaces.length) current = spaces[0].id;
      renderTabs(spaces);
      if (current) return loadSpace(current);

      /* data-comnito="spaceBody" is on the app root itself, so writing an
         empty state into it wiped the whole screen — including the tab strip
         that holds the only "+ Add space" button. The message told people to
         tap a button it had just deleted. Put it after the tabs instead, and
         give it its own button so there are two ways in. */
      var tabs = hook('spaceTabs');
      var host = (tabs && tabs.parentNode) || hook('spaceBody');
      if (!host) return;
      var box = el('div', 'wrap');
      box.innerHTML = empty('No space yet',
        'A space is your page — a Store to sell, Services to take bookings, ' +
        'or a Personal page to meet people.');
      var go = el('button', 'btn btn-primary wide');
      go.textContent = 'Create your first space';
      go.style.marginTop = '4px';
      go.addEventListener('click', createSpace);
      box.appendChild(go);
      if (tabs && tabs.nextSibling) host.insertBefore(box, tabs.nextSibling);
      else host.appendChild(box);
    }).catch(function (e) { toast(e.message); });

    function renderTabs(spaces) {
      var mount = hook('spaceTabs');
      if (!mount) return;
      mount.innerHTML = spaces.map(function (s) {
        return '<button class="spacetab tone-' + spaceTone(s.type) + '" data-id="' + esc(s.id) + '"' +
          (s.id === current ? ' aria-pressed="true"' : '') + '>' +
          esc(s.title || s.type) + '</button>';
      }).join('') + '<button class="spacetab add" data-add="1">+ Add space</button>';

      mount.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b) return;
        if (b.dataset.add) return createSpace();
        current = b.dataset.id;
        $$('.spacetab', mount).forEach(function (x) { x.removeAttribute('aria-pressed'); });
        b.setAttribute('aria-pressed', 'true');
        history.replaceState(null, '', '?space=' + encodeURIComponent(current));
        loadSpace(current);
      });
    }

    /* This used to be two window.prompt calls, the first of which asked the
       person to TYPE "store, services or personal" and rejected anything
       else. Picking from three options is a tap. */
    /* Three steps, each one a sheet: what kind of space, which category, and
       — only if the Worker says something is missing — the directory details
       it cannot create the listing without. The category step exists because
       a space with no category defaults to "Other", which is how nobody
       finds you. */
    /* A space is a TYPE and a NAME. It used to ask for a category too,
       which was the wrong question in the wrong place: the category
       belongs to the listing, not to the shop. A tailor who also sells
       ready-made kurtas was being asked to choose one of them forever.

       The type is fixed once the space exists, because the type is what
       decides the whole shape of the page and the listing limit. */
    function createSpace() {
      var draft = {};

      sheet({
        title: 'Add a space',
        note: 'Each space has its own page, its own listings and its own ' +
              'settings. You choose the categories later, one per listing.',
        choices: [
          { value: 'store',    label: 'Store',
            hint: 'Sell products · up to ' + CFG.QUOTAS.store + ' live listings' },
          { value: 'services', label: 'Services',
            hint: 'Take bookings · up to ' + CFG.QUOTAS.services + ' live listings' },
          { value: 'personal', label: 'Personal',
            hint: 'Meet people · up to ' + CFG.QUOTAS.personal + ' live listings' }
        ],
        fields: [
          { name: 'title', label: 'Name this space',
            placeholder: 'Lakshmana Tailor Shop' }
        ],
        ok: 'Create space'
      }).then(function (r) {
        if (!r) return null;
        draft.type = r.choice;
        draft.title = r.values.title;
        return submitSpace();
      });

      function submitSpace() {
        return API.createSpace(draft)
          .then(function (res) {
            location.href = CFG.PAGES.space + '?space=' + encodeURIComponent(res.space.id);
          })
          .catch(function (e) {
            /* A second space is ₹30/month. Offer it here rather than
               making them find Settings and work out what to buy. */
            if (e.status === 402) {
              return offerUpgrade(e, function () { submitSpace(); });
            }
            var missing = (e.data && e.data.missing) || [];
            if (!missing.length) return toast(e.message, 5000);

            /* Shopify will not put a listing in the directory without these.
               Ask once, then retry the same draft rather than making them
               start over. */
            var labels = {
              city: 'City', area: 'Your area', whatsapp: 'WhatsApp number',
              phone: 'Calling number', pincode: 'PIN code',
              state: 'State', email: 'Email', name: 'Your name'
            };
            return sheet({
              title: 'A few details for the directory',
              note: 'Your space cannot be listed without these. They are saved ' +
                    'to your account, so this is the only time you will be asked.',
              fields: missing.map(function (k) {
                return { name: k, label: labels[k] || k,
                         type: k === 'phone' || k === 'whatsapp' ? 'tel' : 'text',
                         inputmode: k === 'phone' || k === 'whatsapp' || k === 'pincode'
                           ? 'numeric' : undefined };
              }),
              ok: 'Create space'
            }).then(function (extra) {
              if (!extra) return;
              Object.keys(extra.values).forEach(function (k) {
                draft[k] = extra.values[k];
              });
              return submitSpace();
            });
          });
      }
    }

    function loadSpace(id) {
      var body = hook('spaceBody');
      if (body) body.setAttribute('aria-busy', 'true');

      Promise.all([
        API.spaces(),
        API.listings(id)
      ]).then(function (res) {
        var space = (res[0].spaces || []).filter(function (s) { return s.id === id; })[0] || {};
        var items = res[1].listings || [];
        if (body) body.removeAttribute('aria-busy');
        paintHeader(space);
        paintListings(items, space);
        wireSpaceControls(space);
      }).catch(function (e) {
        if (body) body.removeAttribute('aria-busy');
        toast(e.message);
      });
    }

    /* ---- space header -------------------------------------------------
       This used to paint into the prototype's markup, which ships three
       hard-coded headers for a tailor shop in RS Puram. Almost none of
       the hooks it reached for existed: hook('spaceTitle') was null, and
       so were publishSwitch and passwordSwitch — which is exactly why
       tapping Password did nothing at all. The visibility control in the
       markup is a segment labelled "Store link", so it fired
       {name:'store_link'} and the handler was watching for 'password'.

       So the header is generated from the space now. One source of truth,
       every control wired the moment it is written, and room for the two
       things that belong to a space rather than to an account: whether
       you are open today, and what you want to say today. */
    function paintHeader(space) {
      var wrap = $('.headwrap', document);
      if (!wrap) return;
      wrap.innerHTML = '';
      wrap.dataset.spaceId = space.id || '';

      /* The accent stripe along the top of the card is --sp, and the
         type is what colours it: a Store card and a Personal card should
         not look like the same page. */
      var card = el('div', 'spacehead s-' + SPACE_CLASS[space.type || 'store']);
      card.innerHTML = spaceHeadHtml(space);
      wrap.appendChild(card);

      /* Pictures sit at the very top of the card, above the name — which
         is where a cover photo goes on every page anybody has ever seen.
         It scrolls with the card because it is inside it. */
      mountPictureEditor(card, space, function (settings) {
        space.profile_image_url = settings.profile_image_url;
        space.cover_image_url = settings.cover_image_url;
      }, card.firstChild);

      bindControls(card);
      wireHeadCard(card, space);
    }

    function spaceHeadHtml(space) {
      var url = location.origin + publicUrl(space);
      var per = CFG.QUOTAS[space.type] || CFG.QUOTAS.store;
      var live = space.listing_count || 0;
      var vis = space.password_protected ? 'password'
              : (space.followers_visible === false ? 'followers' : 'anyone');
      var closed = !!space.temporarily_closed;

      return '' +
        '<div class="hl">' +
          '<span class="tt"><b>' + esc(space.title || space.type || 'Space') + '</b>' +
            '<span>' + esc(spaceSubtitle(space)) + '</span></span>' +
          '<span class="badge ' + (space.published ? 'badge-ok' : 'badge-draft') + '">' +
            (space.published ? 'Live' : 'Draft') + '</span>' +
        '</div>' +

        '<div class="link">' +
          '<code>' + esc(url) + '</code>' +
          '<button class="copy" data-copy="' + esc(url) + '" aria-label="Copy link">Copy</button>' +
        '</div>' +

        /* Today's state, in one strip. It was three stacked cards with a
           sentence of explanation under each — a third of the screen
           spent telling somebody what "Open today" means. These are the
           three things an owner touches most, so they are three taps in
           a row, and the explanation moves into the toast that fires
           when you actually change one. */
        '<div class="todaystrip">' +
          '<button type="button" class="tchip' +
            (space.open_today !== false && !closed ? ' on' : '') + '"' +
            ' data-today="open"' + (closed ? ' disabled' : '') +
            ' aria-pressed="' + (space.open_today !== false && !closed ? 'true' : 'false') + '">' +
            '<span class="td" aria-hidden="true"></span>' +
            (space.open_today !== false && !closed ? 'Open today' : 'Closed today') +
          '</button>' +

          '<button type="button" class="tchip' + (closed ? ' warn' : '') + '"' +
            ' data-today="closed" aria-pressed="' + (closed ? 'true' : 'false') + '">' +
            (closed ? 'Away' : 'Away for a while') +
          '</button>' +

          '<button type="button" class="tchip wide' +
            (space.announcement ? ' has' : '') + '" data-ann="1">' +
            '<span class="anni" aria-hidden="true">📣</span>' +
            '<span class="annval">' +
              (space.announcement
                ? esc(space.announcement)
                : 'Say what is on today') +
            '</span>' +
          '</button>' +
        '</div>' +

        '<div class="viewas">' +
          '<span class="tx"><b>' + esc(space.title || 'This space') + ' is ' +
            (space.published ? 'live' : 'not live yet') + '</b>' +
            'Turn off to hide the whole space at once</span>' +
          '<button class="switch" role="switch" data-name="publish"' +
            ' data-on="Space is live" data-off="Space hidden"' +
            ' aria-label="Space is live"' +
            ' aria-checked="' + (space.published ? 'true' : 'false') + '"></button>' +
        '</div>' +

        '<div class="visbox">' +
          '<span class="vl">Who can open this link</span>' +
          '<div class="segment vis" data-name="visibility" data-quiet="1"' +
            ' data-label="Who can open this link">' +
            '<button data-value="anyone" aria-pressed="' +
              (vis === 'anyone' ? 'true' : 'false') + '">Anyone</button>' +
            '<button data-value="password" aria-pressed="' +
              (vis === 'password' ? 'true' : 'false') + '">Password</button>' +
            '<button data-value="followers" aria-pressed="' +
              (vis === 'followers' ? 'true' : 'false') + '">Followers</button>' +
          '</div>' +
          '<p class="pwhint"' + (vis === 'password' ? '' : ' hidden') + '>' +
            'Anyone with the link still needs the password. Good for a ' +
            'wholesale rate list, or a page you only want a few people to see. ' +
            '<button class="linkbtn" type="button" data-newpw="1">Change password</button>' +
          '</p>' +
        '</div>' +

        '<div class="cap">' +
          '<span class="capline">Published in this space ' +
            '<b><span class="n" data-comnito="listingCount">' + live + '</span></b></span>' +
          '<span class="bar"><i style="width:' +
            Math.min(100, per ? Math.round((live / per) * 100) : 0) + '%"></i></span>' +
          '<span class="caplabel">' + live + ' of ' + per + ' live in this ' +
            esc(space.type || 'space') + '</span>' +
        '</div>' +

        '<div class="acts">' +
          '<button class="btn btn-primary" data-comnito="addListing">Add a listing</button>' +
          '<button class="btn btn-ghost" data-comnito="openGallery">Gallery</button>' +
        '</div>';
    }

    function spaceSubtitle(space) {
      var bits = [];
      if (space.category) bits.push(space.category);
      if (space.area) bits.push(space.area);
      if (!bits.length) bits.push(CFG.SPACE_BLURB[space.type] || 'Your page');
      return bits.join(' · ');
    }

    /* Everything in the header that is not a plain switch. Wired against
       the card that was just written, so there is no window in which a
       control exists but does nothing. */
    function wireHeadCard(card, space) {
      var ann = $('[data-ann]', card);
      if (ann) ann.addEventListener('click', function () {
        sheet({
          title: 'Today’s announcement',
          note: 'One line, shown on your page and under your name in Near You. ' +
                'It clears itself after a day, so it never goes stale.',
          fields: [{ name: 'announcement', label: 'What is on today',
                     required: false,
                     placeholder: '10% off, white shirts in',
                     value: space.announcement || '' }],
          ok: space.announcement ? 'Update' : 'Post it',
          cancel: space.announcement ? 'Remove' : 'Cancel'
        }).then(function (r) {
          var text = r ? (r.values.announcement || '') : '';
          /* Cancel on an announcement that already exists means "take it
             down" — there is nowhere else to do that. */
          if (!r && !space.announcement) return;
          return API.updateSpace(space.id, { announcement: text })
            .then(function () {
              space.announcement = text;
              toast(text ? 'Posted' : 'Announcement removed');
              paintHeader(space);
            })
            .catch(function (e) { toast(e.message, 4000); });
        });
      });

      /* These two live in the card, so they are rebound on every repaint.
         Wiring them once for the screen would leave them dead the moment
         the header was rewritten — which it is on every space switch. */
      var add = $('[data-comnito="addListing"]', card);
      if (add) add.addEventListener('click', function () {
        location.href = CFG.PAGES.addListing + '?space=' + encodeURIComponent(space.id);
      });

      var gal = $('[data-comnito="openGallery"]', card);
      if (gal) gal.addEventListener('click', function () {
        location.href = CFG.PAGES.gallery + '?space=' + encodeURIComponent(space.id);
      });

      /* The strip's chips are buttons, so they save themselves rather than
         going through the switch event. The sentence that used to sit
         under each one now fires as a toast at the moment it becomes
         true, which is when it means something. */
      var strip = $('.todaystrip', card);
      if (strip) strip.addEventListener('click', function (e) {
        var b = e.target.closest('[data-today]');
        if (!b || b.disabled) return;

        var patch = {}, said = '';
        if (b.dataset.today === 'open') {
          var nowOpen = b.getAttribute('aria-pressed') !== 'true';
          patch.open_today = nowOpen;
          said = nowOpen
            ? 'Open today — customers see a green Open now'
            : 'Closed today — your page says so until you turn it back on';
        } else {
          var away = b.getAttribute('aria-pressed') !== 'true';
          patch.temporarily_closed = away;
          if (away) patch.open_today = false;
          said = away
            ? 'Marked away — enquiry buttons are hidden until you are back'
            : 'Back — your page is taking enquiries again';
        }

        API.updateSpace(space.id, patch).then(function () {
          Object.keys(patch).forEach(function (k) { space[k] = patch[k]; });
          toast(said, 3600);
          paintHeader(space);
        }).catch(function (err) { toast(err.message, 3000); });
      });

      var newpw = $('[data-newpw]', card);
      if (newpw) newpw.addEventListener('click', function (e) {
        e.stopPropagation();
        askPassword(space);
      });
    }

    /* The password sheet, reachable from the segment and from the hint. */
    function askPassword(space) {
      return sheet({
        title: 'Password for this page',
        note: 'Visitors need this to open the link. Share it with the people ' +
              'you want, and change it whenever you like.',
        fields: [{ name: 'password', label: 'Password',
                   placeholder: 'Something you will remember' }],
        ok: 'Set password'
      }).then(function (r) {
        if (!r) return false;
        return API.updateSpace(space.id, {
          password: r.values.password, password_protected: true
        }).then(function () {
          space.password_protected = true;
          toast('Password set — the link now asks for it');
          paintHeader(space);
          return true;
        }).catch(function (err) {
          toast(err.message, 4000);
          return false;
        });
      });
    }

    function paintListings(items, space) {
      var mount = hook('listingList');
      if (!mount) return;
      if (!items.length) {
        mount.innerHTML = empty('No listings yet',
          CFG.COPY.drafts + ' ' + CFG.COPY.plan);
        return;
      }
      mount.innerHTML = items.map(function (i) { return listingCard(i); }).join('');

      mount.addEventListener('click', function (e) {
        var b = e.target.closest('[data-act]');
        if (!b) return;
        var card = b.closest('.lcard'), id = card.dataset.id;
        if (b.dataset.act === 'edit') {
          location.href = CFG.PAGES.addListing + '?id=' + encodeURIComponent(id) +
            '&space=' + encodeURIComponent(space.id);
        }
        if (b.dataset.act === 'toggle') {
          var making = b.textContent.trim() === 'Publish';
          var flip = function () {
            return API.updateListing(id, { status: making ? 'active' : 'hidden' })
              .then(function () {
                b.textContent = making ? 'Hide' : 'Publish';
                toast(making ? 'Published' : 'Hidden');
              });
          };
          flip().catch(function (e) {
            if (e.status === 402) return offerUpgrade(e, function () { flip(); });
            toast(e.message, 2600);
          });
        }
      });
    }

    /* One listener for the screen, not one per space load. This was
       document-level and re-registered on every loadSpace(), so switching
       tabs four times sent four PATCHes for one tap — the later ones
       aimed at spaces the person was no longer looking at. `active` is
       read at event time, so it always means the space on screen. */
    var controlsWired = false;
    function wireSpaceControls(space) {
      activeSpace = space;
      if (controlsWired) return;
      controlsWired = true;

      document.addEventListener('comnito:change', function (e) {
        var sp = activeSpace;
        if (!sp || !sp.id) return;
        var d = e.detail, patch = {};

        if (d.name === 'publish')     patch.published = d.value;
        if (d.name === 'nearby')      patch.nearby_enabled = d.value;
        if (d.name === 'radius')      patch.nearby_radius_km = d.value;
        if (d.name === 'payment')     patch.payment_enabled = d.value;
        if (d.name === 'delivery')    patch.delivery_enabled = d.value;
        if (d.name === 'appointment') patch.appointment_enabled = d.value;
        if (d.name === 'token')       patch.token_enabled = d.value;
        if (d.name === 'followers')   patch.followers_visible = d.value;
        if (d.name === 'open_today')  patch.open_today = d.value;

        /* Closing temporarily also closes today — leaving "Open today"
           on behind a closed sign is the kind of contradiction that
           sends somebody to a shuttered shop. */
        if (d.name === 'temporarily_closed') {
          patch.temporarily_closed = d.value;
          if (d.value) patch.open_today = false;
          toast(d.value ? 'Marked temporarily closed' : 'Open again');
          setTimeout(function () { loadSpace(sp.id); }, 400);
        }

        /* The visibility segment. "Password" is asynchronous — it has to
           ask for the password first — so it saves itself and returns
           rather than riding along with the patch below. */
        if (d.name === 'visibility') {
          if (d.value === 'password') {
            askPassword(sp).then(function (set) {
              if (!set) paintHeader(sp);
            });
            return;
          }
          patch.password = '';
          patch.password_protected = false;
          patch.followers_visible = d.value === 'followers';
          sp.password_protected = false;
        }

        if (!Object.keys(patch).length) return;
        API.updateSpace(sp.id, patch)
          .then(function () {
            Object.keys(patch).forEach(function (k) { sp[k] = patch[k]; });
          })
          .catch(function (err) {
            toast(err.message, err.status === 402 ? 5000 : 2600);
            /* Nothing was saved, so the control must not keep claiming
               it was. Repaint from the space we still believe in. */
            paintHeader(sp);
          });
      });

      /* Arrange — drag the space tabs to reorder, order is persisted */
      var tabs = hook('spaceTabs');
      var arrange = hook('arrangeBtn');
      if (arrange && tabs) arrange.addEventListener('click', function () {
        tabs.classList.toggle('arranging');
        toast(tabs.classList.contains('arranging')
          ? 'Drag your spaces to reorder' : 'Order saved');
        if (!tabs.classList.contains('arranging')) {
          var order = $$('.spacetab[data-id]', tabs).map(function (t) { return t.dataset.id; });
          API.post('/app/spaces/order', { order: order }).catch(quiet('saving space order'));
        }
      });
    }
  };

  /* ------------------------------------------------------------- SETTINGS */
  /* Turn the settings scroll into a grid of tiles.
     Deliberately built from the markup that is already there — read each
     section's own subhead for its icon and title, hide the sections, show
     one at a time. Nothing in the 29 KB of settings markup had to be
     rewritten, which is where the bugs would have come from. */
  /* Short labels for the tiles. The full heading stays on the group itself —
     these only have a third of a phone screen to live in, and breaking
     "Notifications" across a line as "Notificatio/ns" looks broken. */
  var SETTINGS_LABEL = {
    'Discovery & visibility': 'Discovery',
    'How people reach you':   'Contact',
    'Orders & payment':       'Payments',
    'Appointments & tokens':  'Bookings',
    'Followers & privacy':    'Privacy',
    'Deals & confirmation':   'Deals',
    'What you follow & buy':  'Following',
    'Safety & reports':       'Safety',
    'Account & plan':         'Account',
    'Plan & billing':         'Billing',
    'About & legal':          'About'
  };

  var SETTINGS_BLURB = {
    'Discovery & visibility': 'Who can find you, and how far away',
    'How people reach you':   'WhatsApp, calls, and your public numbers',
    'Orders & payment':       'UPI, cash on delivery, and what you accept',
    'Delivery':               'Where you deliver and what it costs',
    'Appointments & tokens':  'Booking slots, daily capacity, tokens',
    'Followers & privacy':    'What followers see, and what stays yours',
    'Notifications':          'What reaches your phone, and when',
    'Add-ons':                'Video calling, storage, your own domain',
    'Deals & confirmation':   'How an order becomes a completed deal',
    'What you follow & buy':  'Your interests and the shops you follow',
    'Safety & reports':       'Blocking, reporting, and who you have hidden',
    'Account & plan':         'Your details, your plan, signing out',
    'Plan & billing':         'What you pay, what for, and when it renews',
    'About & legal':          'Terms, privacy, and how Comnito works'
  };

  function mountSettingsGrid(root) {
    var sections = $$('section', root).filter(function (sec) {
      return !!$('.subhead', sec);
    });
    if (sections.length < 3) return;          /* not the settings screen */

    var host = sections[0].parentNode;
    var grid = el('div', 'setgrid');
    var back = el('button', 'setback');
    back.innerHTML =
      '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" ' +
      'stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
      '<span class="setback-t">All settings</span>';
    back.hidden = true;

    sections.forEach(function (sec, i) {
      var head = $('.subhead', sec);
      var icon = $('svg', head);
      var title = head.textContent.trim();
      var count = $$('.setrow', sec).length;

      var tile = el('button', 'settile');
      tile.type = 'button';
      tile.innerHTML =
        '<span class="settile-i">' + (icon ? icon.outerHTML : '') + '</span>' +
        '<span class="settile-t">' + esc(SETTINGS_LABEL[title] || title) + '</span>' +
        '<span class="settile-n">' +
          esc(SETTINGS_BLURB[title] || (count + ' setting' + (count === 1 ? '' : 's'))) +
        '</span>';
      tile.addEventListener('click', function () { open(i); });
      grid.appendChild(tile);

      sec.hidden = true;
    });

    function open(i) {
      sections.forEach(function (sec, k) { sec.hidden = k !== i; });
      grid.hidden = true;
      back.hidden = false;
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
    }
    function showGrid() {
      sections.forEach(function (sec) { sec.hidden = true; });
      grid.hidden = false;
      back.hidden = true;
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
    }
    back.addEventListener('click', showGrid);

    host.insertBefore(back, sections[0]);
    host.insertBefore(grid, sections[0]);
  }

  /* Verification lives in Settings as a group of its own, built at runtime so
     no markup had to change. Every row is optional and says so. */
  function mountKycGroup(root, host) {
    if (!host || $('.kycgroup', host)) return;

    var sec = el('section', 'kycgroup');
    sec.innerHTML =
      '<p class="subhead">' +
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" ' +
        'stroke-linejoin="round"><path d="M12 3l7 3v6c0 4.4-3 8.3-7 9-4-0.7-7-4.6-7-9V6z"/>' +
        '<path d="M9 12l2 2 4-4"/></svg> Verification</p>' +
      '<p class="kycnote">None of this is required. Your page works either way — ' +
        'a verified mark just means people trust it faster.</p>' +
      '<div class="setgroup kyclist"></div>';

    var list = $('.kyclist', sec);
    var file = el('input');
    file.type = 'file';
    file.accept = 'image/*,application/pdf';
    file.hidden = true;
    sec.appendChild(file);
    var pendingKind = '';

    function paint(kyc) {
      var order = ['whatsapp', 'certificate', 'fssai', 'pan', 'gst', 'age'];
      list.innerHTML = order.map(function (k) {
        var it = kyc[k]; if (!it) return '';
        var state = it.status === 'verified' ? '<span class="kycst ok">Verified</span>'
                  : it.status === 'pending'  ? '<span class="kycst wait">Being checked</span>'
                  : it.status === 'rejected' ? '<span class="kycst no">Not accepted</span>'
                  : '<span class="kycst none">Optional</span>';
        return '<div class="setrow">' +
          '<span class="lbl"><b>' + esc(it.label) + '</b><span>' +
            (it.status === 'rejected' && it.reason ? esc(it.reason)
              : esc(it.hint || (it.needs_document ? 'Upload a photo of it'
                                                  : 'Enter the number'))) +
          '</span></span>' +
          state +
          (it.status === 'verified' ? '' :
            '<button class="kycadd" data-kyc="' + esc(k) + '"' +
              ' data-doc="' + (it.needs_document ? '1' : '0') + '">' +
              (it.status === 'pending' ? 'Replace' : 'Add') + '</button>') +
          '</div>';
      }).join('');
    }

    list.addEventListener('click', function (e) {
      var b = e.target.closest('[data-kyc]');
      if (!b) return;
      var k = b.dataset.kyc;
      var needsDoc = b.dataset.doc === '1';

      if (needsDoc) { pendingKind = k; file.value = ''; file.click(); return; }

      sheet({
        title: 'Add your ' + b.closest('.setrow').querySelector('b').textContent,
        note: 'Optional. It appears on your bills and helps people trust the page.',
        fields: [{ name: 'value', label: 'Number' }],
        ok: 'Save'
      }).then(function (r) {
        if (!r) return;
        API.kycSubmit(k, r.values.value, null)
          .then(function (res) { toast(res.message); return refresh(); })
          .catch(function (err) { toast(err.message, 5000); });
      });
    });

    file.addEventListener('change', function () {
      var f = file.files && file.files[0];
      if (!f || !pendingKind) return;
      toast('Uploading…');
      API.kycSubmit(pendingKind, '', f)
        .then(function (res) { toast(res.message, 5000); return refresh(); })
        .catch(function (err) { toast(err.message, 5000); });
    });

    function refresh() {
      return API.kyc().then(function (r) { paint(r.kyc || {}); })
        .catch(quiet('your verifications'));
    }

    host.appendChild(sec);
    refresh();
  }

  /* ---- Billing --------------------------------------------------------
     Built at runtime, like Verification, so no markup had to change.

     It shows the bill the way it is actually worked out — every line
     named, then the total — because a person who cannot see where ₹99
     comes from assumes the worst. Each line can be dropped from here,
     which is the other half of being allowed to add one. */
  function mountBillingGroup(root, host) {
    if (!host || $('.billgroup', host)) return;

    var sec = el('section', 'billgroup');
    sec.innerHTML =
      '<p class="subhead">' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" ' +
          'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" ' +
          'stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/>' +
          '<path d="M2 10h20"/></svg>Plan &amp; billing</p>' +
      '<div data-bill="1"><p class="fhint">Loading…</p></div>';
    host.appendChild(sec);

    function paint(r) {
      var box = $('[data-bill]', sec);
      var plan = r.plan || {};
      var lines = plan.lines || [];

      if (!plan.active) {
        box.innerHTML =
          '<div class="planbox">' +
            '<p class="plantop"><b>No plan running</b>' +
              '<span>Drafts are free and unlimited. A plan is what makes ' +
              'them visible.</span></p>' +
            '<button class="btn btn-primary wide" data-startplan="1">Start for ' +
              money(CFG.PRICING.baseMonthly) + '/month</button>' +
          '</div>' +
          receiptsHtml(r.payments || []);
        return;
      }

      var auto = r.autopay || {};
      box.innerHTML =
        '<div class="planbox">' +
          '<p class="plantop"><b>' +
            (plan.on_trial ? 'Free trial' : 'Your plan') + '</b>' +
            '<span>' +
              (auto.on
                ? (plan.on_trial
                    ? 'Free until ' + esc(dateWords(plan.until)) +
                      ', then ' + money(plan.monthly_inr) + ' automatically'
                    : 'Renews automatically on ' + esc(dateWords(plan.until)))
                : (plan.on_trial ? 'Free until ' : 'Runs out ') +
                  esc(dateWords(plan.until))) +
            '</span></p>' +

          '<ul class="billlines">' +
            lines.map(function (l, i) {
              return '<li>' +
                '<span class="bl">' + esc(l.label) +
                  (l.detail ? '<em>' + esc(l.detail) + '</em>' : '') + '</span>' +
                '<span class="ba">' + money(l.amount_inr) + '</span>' +
                (i === 0 ? '' :
                  '<button class="linkbtn" data-drop="' + esc(l.key) + '"' +
                    ' data-space="' + esc(l.space_id || '') + '">Remove</button>') +
                '</li>';
            }).join('') +
          '</ul>' +

          '<p class="billtotal"><span>Total</span><b>' +
            money(plan.monthly_inr) + '/month</b></p>' +

          autopayHtml(auto, plan) +
        '</div>' +
        '<div class="addlines">' +
          '<span class="flabel">Add to your plan</span>' +
          [['extra_space', 'Another space', CFG.PRICING.extraSpace],
           ['photo_storage', 'More photo storage', CFG.PRICING.addonMonthly],
           ['video_storage', 'Video storage', CFG.PRICING.addonMonthly],
           ['video_call', 'Video calling', CFG.PRICING.addonMonthly]
          ].map(function (a) {
            return '<button class="addline" data-add="' + a[0] + '">' +
              '<span>' + esc(a[1]) + '</span>' +
              '<em>+' + money(a[2]) + '/month</em></button>';
          }).join('') +
          '<p class="fhint">Added now, billed from your next renewal. ' +
            'Drop anything whenever you like.</p>' +
        '</div>' +
        receiptsHtml(r.payments || []);
    }

    /* Auto-renew is the recommended state, so it is the primary button
       when it is off — but paying by hand stays there, because a
       mandate is a real commitment and some people will not want one. */
    function autopayHtml(auto, plan) {
      if (auto.status === 'halted') {
        return '<div class="autobox warn">' +
            '<p><b>The last payment could not be taken</b>' +
            esc(auto.halted_reason || '') + '</p>' +
            '<button class="btn btn-primary wide" data-startplan="1">Pay ' +
              money(plan.monthly_inr) + ' now</button>' +
            '<button class="linkbtn" data-autopay="1">Set up auto-renew again</button>' +
          '</div>';
      }

      if (auto.on) {
        return '<div class="autobox">' +
            '<p><b>Auto-renew is on</b>' +
              money(auto.monthly_inr || plan.monthly_inr) +
              ' a month, taken automatically. Nothing to remember.</p>' +
            '<button class="linkbtn" data-autooff="1">Turn off auto-renew</button>' +
          '</div>';
      }

      return '<button class="btn btn-primary wide" data-autopay="1">' +
          'Turn on auto-renew</button>' +
        '<p class="fhint" style="margin-top:9px;text-align:center">' +
          money(plan.monthly_inr) + ' a month' +
          (plan.on_trial ? ', starting when your trial ends on ' +
            esc(dateWords(plan.until)) : '') +
          '. Cancel any time.</p>' +
        '<button class="btn btn-ghost wide" style="margin-top:10px" ' +
          'data-startplan="1">Pay for one month instead</button>';
    }

    function receiptsHtml(payments) {
      if (!payments.length) return '';
      return '<div class="receipts">' +
        '<span class="flabel">Payments</span>' +
        payments.slice(0, 12).map(function (p) {
          return '<div class="receipt">' +
            '<span class="rl"><b>' + esc(p.description || 'Comnito') + '</b>' +
              '<span>' + esc(dateWords(p.paid_at)) + '</span></span>' +
            '<span class="ra">' + money(p.amount_inr) + '</span>' +
            '</div>';
        }).join('') + '</div>';
    }

    function dateWords(iso) {
      var t = Date.parse(iso || '');
      if (!isFinite(t)) return 'soon';
      try {
        return new Date(t).toLocaleDateString('en-IN',
          { day: 'numeric', month: 'long' });
      } catch (e) { return new Date(t).toDateString(); }
    }

    sec.addEventListener('click', function (e) {
      var start = e.target.closest('[data-startplan]');
      if (start) return pay({ intent: 'plan' }).then(function (ok) {
        if (ok) refresh();
      });

      if (e.target.closest('[data-autopay]')) {
        return startAutopay().then(function (ok) { if (ok) refresh(); });
      }

      if (e.target.closest('[data-autooff]')) {
        return confirmSheet('Turn off auto-renew?',
          'Nothing more will be taken. Your plan stays live until the month ' +
          'you have paid for runs out, and you can pay by hand after that.',
          'Turn it off').then(function (yes) {
            if (!yes) return;
            return API.post('/app/pay/subscribe/cancel', {})
              .then(function (res) { toast(res.note || 'Auto-renew is off.', 5200); refresh(); })
              .catch(function (err) { toast(err.message, 3500); });
          });
      }

      var add = e.target.closest('[data-add]');
      if (add) {
        return API.post('/app/plan/line', { key: add.dataset.add })
          .then(function (r) { toast(r.note, 5200); refresh(); })
          .catch(function (err) {
            if (err.status === 402) return offerUpgrade(err, refresh);
            toast(err.message, 3500);
          });
      }

      var drop = e.target.closest('[data-drop]');
      if (drop) {
        return confirmSheet('Remove this from your plan?',
          'It stays until your next renewal, then it is gone and your ' +
          'plan costs less.', 'Remove').then(function (yes) {
            if (!yes) return;
            return API.post('/app/plan/line', {
              key: drop.dataset.drop, space_id: drop.dataset.space, add: false
            }).then(function (r) { toast(r.note, 5200); refresh(); })
              .catch(function (err) { toast(err.message, 3500); });
          });
      }
    });

    function refresh() {
      return API.get('/app/payments')
        .then(paint)
        .catch(function (e) {
          $('[data-bill]', sec).innerHTML =
            '<p class="fhint">' + esc(e.message) + '</p>';
        });
    }
    refresh();
  }

  Screens.settings = function (root) {
    var scope = root || document;
    var firstSection = $('section', scope);
    if (firstSection) mountKycGroup(scope, firstSection.parentNode);
    if (firstSection) mountBillingGroup(scope, firstSection.parentNode);
    mountSettingsGrid(scope);
    if (!Session.requireLogin()) return;

    API.me().then(function (r) {
      var m = r.member || {};
      ['name', 'email', 'phone', 'city', 'area', 'pincode'].forEach(function (k) {
        var f = hook(k);
        if (f && m[k]) { if ('value' in f) f.value = m[k]; else f.textContent = m[k]; }
      });
    }).catch(quiet('a background request'));

    API.addons().then(function (r) {
      var a = r.addons || {};
      var map = { domain: 'domainSwitch', video: 'videoSwitch' };
      Object.keys(map).forEach(function (k) {
        var sw = hook(map[k]);
        if (sw) sw.setAttribute('aria-checked', a[k] ? 'true' : 'false');
      });
    }).catch(quiet('a background request'));

    document.addEventListener('comnito:change', function (e) {
      var d = e.detail;

      if (d.name === 'domain') {
        var goDomain = function () {
          API.activateAddon('domain', d.value)
            .then(function () { toast(d.value ? 'Custom domain enabled' : 'Custom domain off'); })
            .catch(function (err) { toast(err.message); });
        };
        if (!d.value) return goDomain();
        return confirmSheet('Use your own domain',
          'Your page moves to your own web address. ' +
          money(CFG.PRICING.domainMonthly) + ' a month, on top of your other charges.',
          'Enable').then(function (yes) {
            if (!yes) { e.target.setAttribute('aria-checked', 'false'); return; }
            goDomain();
          });
      }

      if (d.name === 'video') {
        /* STUB — LATER: this only flips the flag. The call provider
           and the ₹49 / 30-min visitor charge get wired in v2.1. */
        var goVideo = function () {
          API.activateAddon('video', d.value)
            .then(function () { toast(d.value ? 'Video calling activated' : 'Video calling off'); })
            .catch(function (err) { toast(err.message); });
        };
        if (!d.value) return goVideo();
        return confirmSheet('Turn on video calling',
          money(CFG.PRICING.videoCallMonthly) + ' a month. Visitors are charged ' +
          money(CFG.PRICING.videoPerCall) + ' per 30 minutes.',
          'Activate').then(function (yes) {
            if (!yes) { e.target.setAttribute('aria-checked', 'false'); return; }
            goVideo();
          });
      }

      /* everything else is a member-level preference */
      var patch = {};
      patch[d.name] = d.value;
      API.patch('/app/settings', patch).catch(quiet('saving a setting'));
    });

    var out = hook('logout');
    if (out) out.addEventListener('click', function () {
      API.logout().catch(quiet('logout')).then(function () {
        Session.clear();
        location.href = CFG.PAGES.login;
      });
    });
  };

  /* ---------------------------------------------------------- ADD LISTING */
  /* ------------------------------------------------------- ADD LISTING */
  Screens['add-listing'] = function (root) {
    if (!Session.requireLogin()) return;
    root = root || document;

    var spaceId = qs('space'), editId = qs('id');
    var space = null, taxonomy = {};
    var state = {
      space_type: 'store', category: '', subcategory: '',
      values: {}, photos: [null, null, null, null, null], video: null
    };

    /* ---- boot ------------------------------------------------------
       Nothing is drawn until we know which space this is for, because
       every question after that depends on it. */
    API.spaces()
      .then(function (r) {
        var spaces = r.spaces || [];
        if (!spaces.length) return noSpace();
        if (!spaceId) {
          if (spaces.length === 1) spaceId = spaces[0].id;
          else return chooseSpace(spaces);
        }
        return adopt(spaces);
      })
      .catch(function (e) { toast(e.message, 4000); });

    function chooseSpace(spaces) {
      return sheet({
        title: 'Which space is this for?',
        choices: spaces.map(function (sp) {
          return { value: sp.id, label: sp.title || sp.type,
                   hint: (sp.category || CFG.SPACE_BLURB[sp.type] || '') };
        }),
        ok: 'Continue'
      }).then(function (picked) {
        if (!picked) return (location.href = CFG.PAGES.space);
        spaceId = picked.choice;
        history.replaceState(null, '', '?space=' + encodeURIComponent(spaceId));
        return adopt(spaces);
      });
    }

    function noSpace() {
      return sheet({
        title: 'Create a space first',
        note: 'A listing lives inside a space — your Store, Services or ' +
              'Personal page. It takes a moment to make one.',
        ok: 'Go to My Space', cancel: 'Not now'
      }).then(function (go) { if (go) location.href = CFG.PAGES.space; });
    }

    function adopt(spaces) {
      space = spaces.filter(function (sp) { return sp.id === spaceId; })[0] || spaces[0];
      spaceId = space.id;
      state.space_type = space.type || 'store';
      return API.taxonomy()
        .then(function (r) { taxonomy = r.taxonomy || {}; })
        .catch(quiet('listing categories'))
        .then(function () {
          if (editId) return loadExisting();
        })
        .then(build);
    }

    function loadExisting() {
      return API.listings(spaceId).then(function (r) {
        var item = (r.listings || []).filter(function (i) { return i.id === editId; })[0];
        if (!item) return;
        state.category = item.category || state.category;
        state.subcategory = item.subcategory || '';
        var d = item.details || {};
        Object.keys(d).forEach(function (k) { state.values[k] = d[k]; });
        ['product_name', 'description', 'selling_price_inr', 'wholesale_price_inr',
         'stock_quantity', 'service_duration', 'available_hours', 'activity',
         'age', 'requirements', 'delivery_available', 'allow_negotiation',
         'nearby_discovery'].forEach(function (k) {
          if (item[k] !== undefined && item[k] !== '') state.values[k] = item[k];
        });
        if (item.tags && item.tags.length) state.values.tags = item.tags.join(', ');
        state.photos = [0,1,2,3,4].map(function (i) {
          var id = item['product_image_' + (i + 1)];
          if (!id) return null;
          return { url: item['product_image_' + (i + 1) + '_url'] || '', id: id };
        });
      }).catch(quiet('this listing'));
    }

    /* ---- build ------------------------------------------------------ */
    function build() {
      var t = $('#hTitle', root);
      if (t) t.textContent = editId ? 'Edit listing' : 'Add listing';

      paintWhere();
      paintGroups();
      paintPhotos();
      paintOffer();
      wireInputs();
      showStep(0);
    }

    /* Step 1. The space is not asked for — you are already in one, and
       asking again was the complaint. What IS asked is the category, per
       listing, because that is genuinely per listing: a Store can hold
       "Marketplace & Retail · Fashion" and "Vehicles & Mobility · Bikes"
       at the same time, and forcing one category on the whole shop was
       the wrong shape.

       The list is scoped to the space TYPE — a Store sees business
       categories, a Personal space sees personal ones — and if you want
       to list something of another kind, that is a new space. */
    function paintWhere() {
      var host = hook('whereHost');
      if (!host) return;
      var group = taxonomy[CFG.MAIN_TYPE[state.space_type] || 'business'] || {};
      var cats = group.categories || [];
      var found = cats.filter(function (c) { return c.name === state.category; })[0];

      host.innerHTML =
        '<div class="fgroup">' +
          '<div class="subhead-in">' +
            '<span class="lb">Category <span class="req">*</span></span>' +
            '<span class="in">in ' + esc(spaceLabel()) + '</span>' +
          '</div>' +
          (found ? catPickedHtml(found) : catGridHtml(cats)) +
        '</div>' +
        (found ? subGroupHtml(found) : '') +
        '<p class="othernote">Listing something of a different kind — a ' +
          'service, or a personal page? That is a new space, and it gets ' +
          'its own link. <button type="button" class="linkbtn" data-newspace="1">' +
          'Add a space</button></p>';

      bindControls(host);
      wireWhere(host, cats);
    }

    function spaceLabel() {
      return (space && space.title) ||
             (CFG.SPACE_BLURB[state.space_type] || 'your space');
    }

    function catGridHtml(cats) {
      return '<div class="catgrid">' +
        cats.map(function (c) {
          return '<button type="button" class="cattile" data-cat="' + esc(c.name) + '">' +
            '<span class="cattile-i">' + symbolFor(c.name) + '</span>' +
            '<span class="cattile-t">' + esc(c.name) + '</span>' +
            (c.hint ? '<span class="cattile-h">' + esc(c.hint) + '</span>' : '') +
            '</button>';
        }).join('') + '</div>';
    }

    function catPickedHtml(found) {
      return '<div class="catpicked">' +
        '<span class="em">' + symbolFor(found.name) + '</span>' +
        '<span class="tx"><b>' + esc(found.name) + '</b>' +
          '<span>' + esc(found.hint || '') + '</span></span>' +
        '<button type="button" class="chg" data-changecat="1">Change</button>' +
        '</div>';
    }

    function subGroupHtml(found) {
      var subs = found.subcategories || ['Other'];
      return '<div class="fgroup subwrap" style="margin-top:14px">' +
        '<div class="subhead-in">' +
          '<span class="lb">What kind is it? <span class="req">*</span></span>' +
          '<span class="in">in <b>' + esc(found.name) + '</b></span>' +
        '</div>' +
        '<div class="chipset" data-single data-name="subcategory" data-quiet="1"' +
          ' data-label="Subcategory">' +
          subs.map(function (sub) {
            return '<button type="button" class="chip" data-value="' + esc(sub) + '"' +
              ' aria-pressed="' + (state.subcategory === sub ? 'true' : 'false') + '">' +
              esc(sub) + '</button>';
          }).join('') +
        '</div>' +
        '<p class="forwho">This is what people search for in Near You, so pick ' +
          'the word a customer would use.</p>' +
        '</div>';
    }

    function wireWhere(host, cats) {
      host.addEventListener('click', function (e) {
        var tile = e.target.closest('.cattile');
        if (tile) {
          state.category = tile.dataset.cat;
          state.subcategory = '';
          paintWhere();
          repaintSuggestions();
          return;
        }
        if (e.target.closest('[data-changecat]')) {
          state.category = '';
          state.subcategory = '';
          return paintWhere();
        }
        if (e.target.closest('[data-newspace]')) {
          location.href = CFG.PAGES.space;
        }
      });
    }

    function repaintSuggestions() {
      var wrap = $('[data-fwrap="product_name"]', root);
      if (wrap && wrap.__repaintSugg) wrap.__repaintSugg();
    }

    function paintGroups() {
      ['basicsHost', 'detailsHost', 'controlHost'].forEach(function (h) {
        var host = hook(h);
        if (host) host.innerHTML = '';
      });

      LISTING_GROUPS.forEach(function (g) {
        var fields = listingFieldsFor(g.id, state.space_type);
        if (!fields.length) return;
        var host = hook(g.host);
        if (!host) return;
        var box = el('div', 'fgroup');
        box.dataset.group = g.id;
        box.innerHTML = '<span class="flabel">' + esc(g.title) + '</span>' +
          fields.map(function (f) {
            return renderField(f, state.values[f.key], state.space_type);
          }).join('');
        host.appendChild(box);
      });

      var host = hook('basicsHost');
      if (host) mountTitleHelp(host);

      bindControls(hook('basicsHost'));
      bindControls(hook('detailsHost'));
      bindControls(hook('controlHost'));
      applyConditions();
    }

    /* Fields that only make sense once another one is on. Hidden, not
       disabled — a greyed-out box people cannot use is still a box they
       have to read past. */
    function applyConditions() {
      var all = Object.keys(LISTING_FIELDS).reduce(function (acc, k) {
        return acc.concat(LISTING_FIELDS[k]);
      }, []);
      all.forEach(function (f) {
        if (!f.when) return;
        var wrap = $('[data-fwrap="' + f.key + '"]', root);
        if (!wrap) return;
        wrap.hidden = !readValue(f.when);
      });
    }

    /* Title suggestions, built from the space and the subcategory rather
       than from a canned list — "Blouse stitching in RS Puram" is a real
       title somebody would keep. */
    function mountTitleHelp(host) {
      var wrap = $('[data-fwrap="product_name"]', host);
      if (!wrap) return;
      var input = $('[data-f="product_name"]', wrap);
      var row = el('div', 'sugg');
      function options() {
        var sub = state.subcategory || state.category || '';
        var area = (space && space.area) || '';
        var out = [];
        if (sub) out.push(sub);
        if (sub && area) out.push(sub + ' in ' + area);
        if (sub && state.space_type === 'services') out.push(sub + ' at home');
        if (sub && state.space_type === 'store') out.push('Handmade ' + sub.toLowerCase());
        if (space && space.title && sub) out.push(sub + ' · ' + space.title);
        return out.filter(Boolean).slice(0, 4);
      }
      function paint() {
        var opts = options();
        row.hidden = !opts.length;
        row.innerHTML = opts.length
          ? '<span class="suggl">Try:</span>' + opts.map(function (o) {
              return '<button type="button" class="suggb">' + esc(o) + '</button>';
            }).join('')
          : '';
      }
      row.addEventListener('click', function (e) {
        var b = e.target.closest('.suggb');
        if (!b || !input) return;
        input.value = b.textContent;
        state.values.product_name = input.value;
        input.focus();
      });
      wrap.appendChild(row);
      paint();
      wrap.__repaintSugg = paint;
    }

    /* ---- photos -----------------------------------------------------
       This never worked: the slots were static markup with an emoji in
       them and nothing behind. Five slots, a real file input, the same
       crop step the space pictures use, and the two mandatory ones
       marked as such. */
    function paintPhotos() {
      var mount = hook('photoSlots');
      if (!mount) return;

      mount.innerHTML = state.photos.map(function (p, i) {
        if (p && p.url) {
          return '<button type="button" class="pslot filled' +
            (p.pending ? ' pending' : '') + '" data-slot="' + i + '"' +
            ' style="background-image:url(&quot;' + esc(p.url) + '&quot;)">' +
            (p.pending
              ? '<span class="pwait" aria-label="Uploading">Uploading…</span>'
              : '<span class="pdel" data-del="' + i + '" role="button" ' +
                'aria-label="Remove photo">×</span>') +
            '</button>';
        }
        var need = i < 2;
        return '<button type="button" class="pslot' + (need ? ' req' : '') + '"' +
          ' data-slot="' + i + '">＋<span>' +
          (need ? 'Required' : 'Optional') + '</span></button>';
      }).join('') +
      '<button type="button" class="pslot vid" data-video="1">' +
        (state.video ? '🎬<span>Video added</span>' : '🎬<span>Add video</span>') +
      '</button>';

      var file = $('input[type=file]', mount.parentNode);
      if (!file) {
        file = el('input');
        file.type = 'file';
        file.accept = 'image/*';
        file.hidden = true;
        mount.parentNode.appendChild(file);
        file.addEventListener('change', function () {
          var f = file.files && file.files[0];
          var slot = Number(file.dataset.slot || 0);
          file.value = '';
          if (!f) return;
          uploadPhoto(f, slot);
        });
      }

      mount.addEventListener('click', function (e) {
        var del = e.target.closest('[data-del]');
        if (del) {
          e.stopPropagation();
          state.photos[Number(del.dataset.del)] = null;
          return paintPhotos();
        }
        if (e.target.closest('[data-video]')) return askVideo();
        var b = e.target.closest('[data-slot]');
        if (!b) return;
        file.dataset.slot = b.dataset.slot;
        file.click();
      });
    }

    /* Photos go through the gallery, which is where the Worker already
       stores media and enforces the per-listing cap. Cropped to a square
       first so a wall of listings does not come out ragged. */
    function uploadPhoto(f, slot) {
      if (f.size > CFG.LIMITS.photoBytes) {
        return toast('That photo is over ' +
          Math.round(CFG.LIMITS.photoBytes / (1024 * 1024)) + ' MB. Try another.', 4000);
      }
      var local = '';
      pickCrop(f, 'photo')
        .then(function (ready) {
          if (!ready) return null;
          /* Show it straight away. Shopify processes an upload after it
             answers, so its URL is often still null on the way back — the
             photo really had uploaded, the slot was just empty, which is
             indistinguishable from a failure. The local copy holds the
             slot until the real URL arrives on the next read. */
          try {
            local = URL.createObjectURL(ready);
            state.photos[slot] = { url: local, id: '', pending: true };
            paintPhotos();
          } catch (e) {}
          toast('Uploading…');
          return API.galleryUpload(ready, 'photo', spaceId);
        })
        .then(function (r) {
          if (!r) return;
          var m = r.media || r.item || {};

          /* THE reference the listing stores is Shopify's file id, not the
             gallery's own media id. Sending app_media_… produced "Value
             must be a file reference string" from Shopify and lost the
             whole listing at save time — the upload had worked, so nothing
             pointed at the real cause. */
          var fileId = m.file_id || m.id_file || '';
          if (!fileId) {
            state.photos[slot] = null;
            paintPhotos();
            return toast('That photo did not attach. Try it again.', 4500);
          }

          state.photos[slot] = {
            url: m.url || local,
            id: fileId,
            media_id: m.id || ''
          };
          paintPhotos();
          toast('Photo added');
        })
        .catch(function (e) {
          state.photos[slot] = null;
          paintPhotos();
          toast(e.message, 4500);
        });
    }

    /* One video, ₹19 once. Not five, because a wall of autoplaying
       video on a phone on Indian mobile data is a worse page than no
       video at all. */
    function askVideo() {
      return sheet({
        title: 'Add a video',
        note: 'One short video per listing. ' + CFG.COPY.videoStorage +
              ' A blouse turning in the light gets more enquiries than a ' +
              'photo of it folded.',
        ok: 'Continue', cancel: 'Not now'
      }).then(function (go) {
        if (!go) return;
        return API.post('/app/plan/line', { key: 'video_storage' })
          .then(function (r) { toast(r.note || 'Video storage added.', 5200); })
          .catch(function (e) {
            if (e.status === 402) return offerUpgrade(e, null);
            toast(e.message, 3000);
          });
      });
    }

    /* The plan card. It is shown once, on the last step, and it explains
       the whole bill rather than only the number in front of you — a
       price that turns out to have parts is worse than a bigger price
       that was honest from the start. */
    function paintOffer() {
      var card = hook('offerCard');
      if (!card) return;
      card.innerHTML =
        '<div class="hd"><span class="amt">' + money(CFG.PRICING.baseMonthly) +
          '</span><b>a month, everything included</b></div>' +
        '<ul>' +
          ['One space, with publishing included — no charge per listing',
           'Your own page — ' + esc(location.host) + esc(publicUrl(space || {})) +
             ' — to share anywhere',
           'You show up in Near You searches around you',
           'Orders, enquiries and messages, with no commission ever'
          ].map(function (line) {
            return '<li><span>' + line + '</span></li>';
          }).join('') +
        '</ul>' +
        '<p class="ft">' + esc(CFG.COPY.drafts) + ' ' +
          esc(CFG.COPY.extraSpace) + ' ' + esc(CFG.COPY.addons) + ' ' +
          esc(CFG.COPY.passwordShare) + ' ' + esc(CFG.COPY.domain) + '</p>';
    }

    /* ---- reading the form ------------------------------------------ */
    function readValue(key) {
      var node = $('[data-f="' + key + '"]', root);
      if (node) {
        if (node.classList.contains('switch')) {
          return node.getAttribute('aria-checked') === 'true';
        }
        return node.value;
      }
      var chips = $('.chipset[data-name="' + key + '"]', root);
      if (chips) {
        return $$('.chip[aria-pressed="true"]', chips).map(function (c) {
          return c.dataset.value;
        });
      }
      return state.values[key];
    }

    function wireInputs() {
      root.addEventListener('input', function (e) {
        var f = e.target.closest('[data-f]');
        if (!f) return;
        state.values[f.dataset.f] = f.value;
        if (f.dataset.f === 'cost_per_item_inr' || f.dataset.f === 'margin_percent') {
          recalcPrice(f.dataset.f);
        }
      });

      document.addEventListener('comnito:change', function (e) {
        var d = e.detail;
        if (d.name === 'subcategory') {
          state.subcategory = d.value;
          repaintSuggestions();
          return;
        }
        state.values[d.name] = d.value;
        applyConditions();
      });
    }

    /* Cost and margin drive the selling price, and typing a selling
       price drives the margin back. Comnito's cut is zero and the
       calculator says so out loud, because "commission" is the first
       thing every seller asks about. */
    function recalcPrice() {
      var cost = Number(readValue('cost_per_item_inr')) || 0;
      var margin = Number(readValue('margin_percent'));
      if (!cost || !isFinite(margin)) return;
      var sell = Math.round(cost * (1 + margin / 100));
      var node = $('[data-f="selling_price_inr"]', root);
      if (node) { node.value = sell; state.values.selling_price_inr = sell; }
    }

    function collect(publish) {
      var body = {
        space_id: spaceId,
        listing_type: state.space_type === 'services' ? 'service' : 'product',
        category: state.category,
        subcategory: state.subcategory,
        status: publish ? 'active' : 'draft',
        details: {}
      };

      Object.keys(LISTING_FIELDS).forEach(function (g) {
        listingFieldsFor(g, state.space_type).forEach(function (f) {
          var v = readValue(f.key);
          if (v === undefined || v === '') return;
          if (f.top) body[f.key] = v;
          else body.details[f.key] = v;
        });
      });

      if (body.tags) {
        body.tags = String(body.tags).split(',')
          .map(function (t) { return t.trim(); }).filter(Boolean).slice(0, 15);
      }

      /* Only real Shopify file references travel. A slot still showing a
         local preview has nothing to send, and sending its blob URL is
         what Shopify rejected. */
      state.photos.forEach(function (p, i) {
        if (p && p.id && /^gid:\/\//.test(p.id)) {
          body['image_' + (i + 1) + '_id'] = p.id;
        }
      });

      return body;
    }

    function photoCount() {
      return state.photos.filter(function (p) {
        return p && p.id && /^gid:\/\//.test(p.id);
      }).length;
    }

    /* ---- steps ------------------------------------------------------ */
    var steps = $$('[data-step]', root);
    var dots  = $$('.steps .s', root);
    var note  = $('#stepNote', root);
    var next  = hook('publish');
    var back  = $('#back2', root);
    var draft = hook('saveDraft');
    var stepI = 0;
    var LAST  = Math.max(0, steps.length - 1);

    var NOTES = [
      'Step 1 of 3 · What kind of listing is this?',
      'Step 2 of 3 · Photos, and what it is called',
      'Step 3 of 3 · The details, and who can see it'
    ];

    function showStep(i) {
      stepI = Math.max(0, Math.min(i, LAST));
      steps.forEach(function (sec, k) { sec.hidden = k !== stepI; });
      dots.forEach(function (d, k) { d.classList.toggle('on', k <= stepI); });
      if (note) note.textContent = NOTES[stepI] || '';
      if (back) back.hidden = stepI === 0;
      if (next) next.textContent = stepI === LAST ? 'Publish' : 'Continue';
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
    }

    if (back) back.addEventListener('click', function () { showStep(stepI - 1); });

    /* The header arrow walks the same steps. It returns true while there
       is a step to go back to, and false on step 1 so the arrow then
       leaves the screen as it always did. */
    stepBack = function () {
      if (stepI === 0) return false;
      showStep(stepI - 1);
      return true;
    };
    if (draft) draft.addEventListener('click', function () { save(false); });

    if (next) next.addEventListener('click', function () {
      if (stepI === 0) {
        if (!state.category) return toast('Pick a category first');
        if (!state.subcategory) return toast('Which kind is it? Pick one to carry on');
        return showStep(1);
      }
      if (stepI === 1) {
        if (!readValue('product_name')) return toast('Give this listing a name');
        return showStep(2);
      }
      save(true);
    });

    function save(publish) {
      /* Say it here rather than letting the Worker say it after a round
         trip — and never lose the typing either way. */
      if (publish && photoCount() < 2) {
        toast('Two photos are needed to go live. Saving as a draft instead.', 5000);
        return save(false);
      }

      var body = collect(publish);
      if (!body.product_name) return toast('Give this listing a name');

      var btn = publish ? next : draft;
      if (btn) { btn.disabled = true; btn.dataset.prev = btn.textContent; btn.textContent = 'Saving…'; }
      function release() {
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.prev || 'Continue'; }
      }

      var p = editId ? API.updateListing(editId, body) : API.createListing(body);
      function leave(delay) {
        setTimeout(function () {
          location.href = CFG.PAGES.space + '?space=' + encodeURIComponent(spaceId);
        }, delay || 900);
      }

      p.then(function () {
        toast(publish ? 'Published' : 'Saved as a draft');
        leave(600);
      }).catch(function (e) {
        release();

        /* Publishing is the paid step. Never make somebody retype a
           listing because of it — the work is saved as a draft FIRST,
           and only then are they offered the plan. If they pay, it
           publishes; if they do not, the draft is still there. */
        if (e.status === 402 && publish) {
          body.status = 'draft';
          var fallback = editId ? API.updateListing(editId, body) : API.createListing(body);
          return fallback.then(function (saved) {
            var id = editId || (saved && saved.listing && saved.listing.id);
            return offerUpgrade(e, null).then(function (paid) {
              if (!paid) {
                toast('Saved as a draft. ' + e.message, 5200);
                return leave();
              }
              /* Paid — finish the job they actually asked for. */
              if (!id) return leave();
              return API.updateListing(id, { status: 'active' })
                .then(function () { toast('Published'); leave(); })
                .catch(function (err2) { toast(err2.message, 4000); leave(); });
            });
          }).catch(function () { toast(e.message, 5000); });
        }

        toast(e.message, 3000);
      });
    }
  };


  function symbolFor(name) {
    var map = {
      'Marketplace & Retail': '🛒', 'Business & Industrial': '🏭',
      'Vehicles & Mobility': '🚗', 'Food & Hospitality': '🍽',
      'Home & Property': '🏠', 'Handmade & Handcraft': '🧶',
      'Agriculture': '🌾', 'Travel, Tourism & Stay': '✈',
      'Education & Professional': '🎓', 'Health & Wellness': '💪',
      'Clinic & Health Services': '🩺', 'Home Services': '🔧',
      'Jobs & Hiring': '💼', 'Health & Care': '🩺',
      'Professional & Business Services': '📈', 'Education & Training': '📚',
      'Cab & Mobility': '🚕', 'Hospitality Services': '🏨',
      'Matrimony': '💍', 'Relationship': '❤', 'Companionship': '🤝',
      'Only you & me': '✨', 'Other': '➕'
    };
    return map[name] || '➕';
  }

  /* -------------------------------------------------------------- GALLERY */
  /* -------------------------------------------------------------- GALLERY
     Rebuilt. The old screen looked for hooks the markup never had — no
     file input existed anywhere on the page, so "Add photo or video" was
     a button wired to nothing, which is what "images are not uploading"
     turned out to be.

     It also showed only uploads, while the covers, logos and listing
     photos — most of the pictures a person actually has — lived somewhere
     else entirely and the screen said "your gallery is empty" over the
     top of them. Everything is here now, with the ones that belong to
     something else marked as such rather than hidden. */
  Screens.gallery = function (root) {
    if (!Session.requireLogin()) return;
    root = root || document;

    var spaceId = qs('space');
    var host = hook('galleryBody');
    var media = [], attached = [], listings = [], storage = null, filter = 'all';

    if (!host) return;

    host.innerHTML =
      '<div class="galtools">' +
        '<button class="btn btn-primary wide" data-add="1">Add a photo</button>' +
        '<button class="btn btn-ghost" data-addvideo="1">Add a video</button>' +
      '</div>' +
      '<div class="storage" data-store="1"></div>' +
      '<div class="chipset scroll galfilter" data-single data-name="galfilter"' +
        ' data-quiet="1" data-label="Show">' +
        [['all', 'Everything'], ['mine', 'Uploads'], ['listing', 'On listings'],
         ['space', 'Covers & logos'], ['hidden', 'Hidden']]
          .map(function (f, i) {
            return '<button type="button" class="chip" data-value="' + f[0] + '"' +
              ' aria-pressed="' + (i === 0 ? 'true' : 'false') + '">' + f[1] + '</button>';
          }).join('') +
      '</div>' +
      '<div class="galgrid" data-grid="1"></div>' +
      '<ul class="limits" data-limits="1"></ul>';

    var file = el('input');
    file.type = 'file';
    file.hidden = true;
    host.appendChild(file);
    bindControls(host);

    var grid = $('[data-grid]', host);

    function load() {
      Promise.all([API.gallery(spaceId), API.listings(spaceId)])
        .then(function (r) {
          media    = r[0].media || [];
          attached = r[0].attached || [];
          storage  = r[0].storage || null;
          listings = r[1].listings || [];
          paint();
        })
        .catch(function (e) { toast(e.message); });
    }

    /* One list, in the order somebody thinks about their pictures: the
       ones they uploaded, then the ones the app made from them. */
    function visible() {
      var all = media.map(function (m) {
        return { item: m, source: 'upload' };
      }).concat(attached.map(function (a) {
        return { item: a, source: a.source };
      }));

      return all.filter(function (row) {
        var m = row.item;
        if (filter === 'hidden')  return !!m.hidden;
        if (m.hidden && filter !== 'all') return false;
        if (filter === 'mine')    return row.source === 'upload';
        if (filter === 'listing') return row.source === 'listing' ||
                                         (m.tags && m.tags.length);
        if (filter === 'space')   return row.source === 'cover' || row.source === 'logo';
        return true;
      });
    }

    function paint() {
      paintStorage();
      paintLimits();

      var rows = visible();
      if (!rows.length) {
        grid.innerHTML = empty(
          filter === 'all' ? 'No pictures yet' : 'Nothing here',
          filter === 'all'
            ? 'Add a photo and it is reusable on every listing — you never ' +
              'upload the same picture twice.'
            : 'Try another filter.');
        return;
      }

      grid.innerHTML = rows.map(function (row) {
        var m = row.item;
        var locked = !!m.locked;
        return '<figure class="gitem' + (m.hidden ? ' is-hidden' : '') +
            '" data-id="' + esc(m.id) + '">' +
          (m.kind === 'video'
            ? '<video src="' + esc(m.url) + '" muted playsinline></video>'
            : '<img src="' + esc(m.url) + '" alt="' + esc(m.label || '') + '" loading="lazy">') +
          (locked
            ? '<span class="gitem-n">' + esc(sourceLabel(row.source)) + '</span>'
            : (m.tags && m.tags.length
                ? '<span class="gitem-n">on ' + m.tags.length + ' listing' +
                  (m.tags.length === 1 ? '' : 's') + '</span>'
                : (m.shared ? '<span class="gitem-n">shared</span>' : ''))) +
          '<figcaption class="gitem-b">' +
            (locked
              ? '<span class="ibtn ghost">' + esc(m.label || '') + '</span>'
              : '<button class="ibtn" data-act="tag">Tag</button>' +
                '<button class="ibtn" data-act="share">' +
                  (m.shared ? 'Unshare' : 'Share') + '</button>' +
                '<button class="ibtn" data-act="vis">' +
                  (m.hidden ? 'Show' : 'Hide') + '</button>' +
                '<button class="ibtn" data-act="del">Delete</button>') +
          '</figcaption>' +
          '</figure>';
      }).join('');
    }

    function sourceLabel(src) {
      return src === 'cover' ? 'Cover' : src === 'logo' ? 'Logo' : 'On a listing';
    }

    /* Two meters, because they are two different problems and one bar
       covering both told a person nothing about which one they had
       filled. */
    function paintStorage() {
      var box = $('[data-store]', host);
      if (!box || !storage) return;
      var price = money(storage.pack_price_inr || CFG.PRICING.addonMonthly);
      box.innerHTML =
        meterHtml('Photos', storage.photo, price) +
        meterHtml('Video', storage.video, price);
    }

    function meterHtml(name, m, price) {
      if (!m) return '';
      var pct = m.allowed_bytes
        ? Math.min(100, Math.round((m.used_bytes / m.allowed_bytes) * 100))
        : (m.used_bytes ? 100 : 0);
      var none = !m.allowed_bytes;
      return '<div class="meter' + (none ? ' off' : '') + '">' +
        '<span class="sl">' + esc(name) + ' <b>' +
          (none ? 'not switched on' : mb(m.used_bytes) + ' of ' + mb(m.allowed_bytes)) +
        '</b></span>' +
        '<span class="bar"><i style="width:' + pct + '%"></i></span>' +
        '<p class="fhint">' + esc(m.note || '') + ' ' +
          (none ? mb(m.pack_bytes) + ' is ' + price + '/month.'
                : 'Another ' + mb(m.pack_bytes) + ' is ' + price + '/month.') +
        '</p></div>';
    }

    function paintLimits() {
      var ul = $('[data-limits]', host);
      if (!ul) return;
      ul.innerHTML =
        '<li><span>Photos up to <b>' +
          Math.round(CFG.LIMITS.photoBytes / 1048576) + ' MB</b> each, JPG, PNG or WebP. ' +
          'Anything larger is resized rather than refused.</span></li>' +
        '<li><span>Videos up to <b>' +
          Math.round(CFG.LIMITS.videoBytes / 1048576) + ' MB</b> each, and one video ' +
          'may sit on each listing.</span></li>' +
        '<li><span>' + esc(CFG.COPY.videoStorage) + '</span></li>' +
        '<li><span>Hidden pictures stay in your gallery but come off every ' +
          'page. Shared ones appear on your public page even when they are ' +
          'not attached to a listing.</span></li>';
    }

    function mb(bytes) {
      var n = Number(bytes || 0) / 1048576;
      return (n >= 100 ? Math.round(n) : Math.round(n * 10) / 10) + ' MB';
    }

    document.addEventListener('comnito:change', function (e) {
      if (e.detail.name !== 'galfilter') return;
      filter = e.detail.value || 'all';
      paint();
    });

    /* ---- uploading --------------------------------------------------- */
    host.addEventListener('click', function (e) {
      if (e.target.closest('[data-add]')) {
        file.accept = 'image/*';
        file.dataset.kind = 'photo';
        file.value = '';
        return file.click();
      }
      if (e.target.closest('[data-addvideo]')) {
        return confirmSheet('Add a video?',
          CFG.COPY.videoStorage, 'Continue').then(function (yes) {
            if (!yes) return;
            file.accept = 'video/*';
            file.dataset.kind = 'video';
            file.value = '';
            file.click();
          });
      }
    });

    file.addEventListener('change', function () {
      var f = file.files && file.files[0];
      if (!f) return;
      var isVideo = file.dataset.kind === 'video' || /^video\//.test(f.type);
      var cap = isVideo ? CFG.LIMITS.videoBytes : CFG.LIMITS.photoBytes;
      if (f.size > cap) {
        file.value = '';
        return toast((isVideo ? 'Videos' : 'Photos') + ' must be under ' +
          Math.round(cap / 1048576) + ' MB', 4000);
      }
      toast('Uploading…');
      var send = function () {
        return API.galleryUpload(f, isVideo ? 'video' : 'photo', spaceId)
          .then(function () { file.value = ''; load(); toast('Added to your gallery'); });
      };
      send().catch(function (err) {
        file.value = '';
        if (err.status === 402) return offerUpgrade(err, function () { load(); });
        toast(err.message, 3000);
      });
    });

    /* ---- per-item actions -------------------------------------------- */
    grid.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b) return;
      var id = b.closest('.gitem').dataset.id;
      var m = media.filter(function (x) { return x.id === id; })[0];
      if (!m) return;

      if (b.dataset.act === 'vis') {
        return API.galleryUpdate(id, { hidden: !m.hidden })
          .then(function () { m.hidden = !m.hidden; paint(); })
          .catch(function (err) { toast(err.message); });
      }

      if (b.dataset.act === 'share') {
        return API.galleryUpdate(id, { shared: !m.shared })
          .then(function () {
            m.shared = !m.shared;
            toast(m.shared
              ? 'Shared — it now appears on your public page'
              : 'No longer shared');
            paint();
          })
          .catch(function (err) { toast(err.message); });
      }

      if (b.dataset.act === 'del') {
        return confirmSheet('Remove this from your gallery?',
          'It comes off every listing it is tagged to. This cannot be undone.',
          'Remove', true).then(function (yes) {
            if (!yes) return;
            API.galleryDelete(id)
              .then(function () {
                media = media.filter(function (x) { return x.id !== id; });
                paint();
              })
              .catch(function (err) { toast(err.message); });
          });
      }

      if (b.dataset.act === 'tag') return tagSheet(m);
    });

    /* Tagging is a choice among the member's listings, so it is a sheet
       rather than a select somewhere else on the page that has to be set
       first — that ordering was the part nobody guessed. */
    function tagSheet(m) {
      if (!listings.length) {
        return toast('No listings yet — a photo can be tagged once you have one', 4000);
      }
      return sheet({
        title: 'Put this photo on a listing',
        note: 'Up to ' + CFG.LIMITS.galleryPhotosPerListing + ' photos and one ' +
              'video on each listing. Tagging does not use your storage.',
        choices: listings.map(function (l) {
          var c = countFor(l.id);
          var on = (m.tags || []).indexOf(l.id) > -1;
          return { value: l.id, label: (on ? '✓ ' : '') + (l.product_name || 'Listing'),
                   hint: c.photos + ' of ' + CFG.LIMITS.galleryPhotosPerListing +
                         ' photos' + (on ? ' · already on this one' : '') };
        }),
        ok: 'Done'
      }).then(function (r) {
        if (!r || !r.choice) return;
        var listingId = r.choice;
        var already = (m.tags || []).indexOf(listingId) > -1;
        var c = countFor(listingId);
        if (!already) {
          if (m.kind === 'video' && c.videos >= CFG.LIMITS.galleryVideosPerListing) {
            return toast('That listing already has its video');
          }
          if (m.kind !== 'video' && c.photos >= CFG.LIMITS.galleryPhotosPerListing) {
            return toast('That listing is full at ' +
              CFG.LIMITS.galleryPhotosPerListing + ' photos');
          }
        }
        return API.galleryTag(m.id, listingId, !already)
          .then(function () {
            m.tags = m.tags || [];
            if (already) m.tags = m.tags.filter(function (t) { return t !== listingId; });
            else m.tags.push(listingId);
            toast(already ? 'Taken off that listing' : 'Added to that listing');
            paint();
          })
          .catch(function (err) { toast(err.message); });
      });
    }

    function countFor(listingId) {
      var photos = 0, videos = 0;
      media.forEach(function (m) {
        if ((m.tags || []).indexOf(listingId) < 0) return;
        if (m.kind === 'video') videos++; else photos++;
      });
      return { photos: photos, videos: videos };
    }

    load();
  };

  /* ------------------------------------------------------------- REQUESTS
     Enquiries, orders and direction requests — the deal flow.
     Both sides confirm; only then does the deal count go up.             */
  Screens.requests = function () {
    if (!Session.requireLogin()) return;

    var role = 'owner';
    var mount = hook('requestList');

    var tabs = hook('requestTabs');
    if (tabs) tabs.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      $$('button', tabs).forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
      b.setAttribute('aria-pressed', 'true');
      role = b.dataset.role || 'owner';
      load();
    });

    function load() {
      if (!mount) return;
      mount.setAttribute('aria-busy', 'true');
      API.requests(role).then(function (r) {
        mount.removeAttribute('aria-busy');
        var list = r.requests || [];
        if (!list.length) {
          mount.innerHTML = empty(
            role === 'owner' ? 'No requests yet' : 'You have not asked for anything yet',
            role === 'owner' ? 'Enquiries from visitors land here.'
                             : 'Requests you send appear here.');
          return;
        }
        mount.innerHTML = list.map(dealCard).join('');
      }).catch(function (e) {
        mount.removeAttribute('aria-busy');
        mount.innerHTML = empty('Could not load', e.message);
      });
    }

    function dealCard(d) {
      var mine = role === 'owner';
      var actions = [];

      if (d.status === 'open' && mine) {
        actions.push(['bill', 'Send bill']);
        actions.push(['decline', 'Decline']);
      }
      if (d.status === 'billed' && !mine) {
        actions.push(['approve', 'Approve ' + money(d.amount)]);
        actions.push(['dispute', 'Ask a change']);
      }
      if (d.status === 'paid') {
        var confirmed = mine ? d.owner_confirmed : d.visitor_confirmed;
        if (!confirmed) actions.push(['confirm', 'Confirm done']);
      }
      actions.push(['message', 'Message']);
      actions.push(['report', 'Report']);

      return '<article class="deal" data-id="' + esc(d.id) + '" data-status="' + esc(d.status) + '">' +
        '<header class="deal-h">' +
          '<span class="deal-who">' + esc(mine ? (d.from_name || 'Visitor') : (d.to_name || 'Business')) + '</span>' +
          '<span class="deal-st st-' + esc(d.status) + '">' + esc(statusLabel(d)) + '</span>' +
        '</header>' +
        '<p class="deal-b">' + esc(d.note || d.kind || '') + '</p>' +
        (d.amount ? '<p class="deal-amt">' + money(d.amount) + '</p>' : '') +
        '<p class="deal-when">' + ago(d.created_at) + '</p>' +
        '<footer class="deal-f">' + actions.map(function (a) {
          return '<button class="pabtn' + (a[0] === 'confirm' || a[0] === 'approve' ? ' lead' : '') +
            '" data-act="' + a[0] + '">' + esc(a[1]) + '</button>';
        }).join('') + '</footer></article>';
    }

    function statusLabel(d) {
      if (d.status === 'open')     return 'Waiting on you';
      if (d.status === 'billed')   return 'Bill sent';
      if (d.status === 'paid')     return (d.owner_confirmed ? 'Seller confirmed' : '') ||
                                          (d.visitor_confirmed ? 'Buyer confirmed' : 'Paid');
      if (d.status === 'complete') return 'Done · counted';
      if (d.status === 'declined') return 'Declined';
      return d.status;
    }

    if (mount) mount.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b) return;
      var card = b.closest('.deal'), id = card.dataset.id, act = b.dataset.act;

      if (act === 'message') {
        return (location.href = CFG.PAGES.messages + '?deal=' + encodeURIComponent(id));
      }
      if (act === 'report') {
        return API.report({ kind: 'deal', target_id: id })
          .then(function () { toast('Reported'); })
          .catch(function (e) { toast(e.message); });
      }
      if (act === 'bill') {
        return sheet({
          title: 'Send a bill',
          note: 'They approve the amount before anything is paid.',
          fields: [{ name: 'amount', label: 'Amount in ₹', type: 'text',
                     inputmode: 'numeric', placeholder: '450' }],
          ok: 'Send bill'
        }).then(function (r) {
          if (!r) return;
          var amount = Number(String(r.values.amount).replace(/[^0-9.]/g, ''));
          if (!amount) return toast('Enter an amount');
          API.updateRequest(id, { action: 'bill', amount: amount })
            .then(function () { toast('Bill sent for approval'); load(); })
            .catch(function (e) { toast(e.message); });
        });
      }
      if (act === 'approve') {
        return API.updateRequest(id, { action: 'approve' })
          .then(function () { toast('Approved. Pay, then both confirm.'); load(); })
          .catch(function (e) { toast(e.message); });
      }
      if (act === 'dispute') {
        return (location.href = CFG.PAGES.messages + '?deal=' + encodeURIComponent(id));
      }
      if (act === 'decline') {
        return API.updateRequest(id, { action: 'decline' })
          .then(function () { load(); }).catch(function (e) { toast(e.message); });
      }
      if (act === 'confirm') {
        return API.updateRequest(id, { action: 'confirm', role: role })
          .then(function (r) {
            toast(r.complete ? 'Both confirmed — deal counted' : 'Confirmed. Waiting on the other side.');
            load();
          })
          .catch(function (e) { toast(e.message); });
      }
    });

    load();
  };

  /* ------------------------------------------------------ PUBLIC PAGES
     Standalone customer-facing Store / Services / Personal pages.
     These are the pages an owner shares. No owner controls here.       */

  function publicPage(kind) {
    return function () {
      var handle = publicHandle();
      if (!handle) {
        var m0 = hook('pubBody') || $('main') || document.body;
        m0.innerHTML = empty('No page here',
          'This address needs the name of the shop or person on the end of ' +
          'it — the link they shared with you will have it.');
        return;
      }

      var pass = '';
      load();

      function load() {
        API.publicSpace(handle, pass)
          .then(function (r) {
            if (r.locked) return askPassword();
            paint(r);
          })
          .catch(function (e) {
            var m = hook('pubBody') || $('main') || document.body;
            /* Four different things arrive here and they are not the same
               thing to the person reading. "Not published yet" is the
               owner's problem and they can fix it; the rest are dead
               ends, and saying so plainly beats a blank page. */
            var title = e.status === 404 ? 'This page is not open yet'
                      : 'Page not available';
            m.innerHTML = empty(title, e.message ||
              'Ask whoever shared this link to check it.');
          });
      }

      function askPassword() {
        var m = hook('pubBody') || $('main') || document.body;
        m.innerHTML =
          '<div class="lockwrap">' +
            '<p class="lock-t">This page is private</p>' +
            '<input class="in" id="pw" type="password" placeholder="Enter password" autocomplete="off">' +
            '<button class="btn" id="pwGo">Open</button>' +
          '</div>';
        $('#pwGo').addEventListener('click', function () {
          pass = $('#pw').value;
          load();
        });
      }

      function paint(r) {
        var s = r.space || {}, items = r.listings || [], gal = r.gallery || [];

        /* The page already had a .cover band and an .av circle waiting for
           real pictures. Fill them if the shop has uploaded any; otherwise
           they stay as the coloured band and initial they were. */
        var cover = $('.cover');
        if (cover && s.cover_image_url) {
          cover.style.backgroundImage = 'url("' + s.cover_image_url + '")';
          cover.classList.add('has-photo');
        }
        var av = $('.av');
        if (av) {
          if (s.profile_image_url) {
            av.style.backgroundImage = 'url("' + s.profile_image_url + '")';
            av.classList.add('has-photo');
            av.textContent = '';
          } else {
            av.textContent = (s.title || 'C').trim().charAt(0).toUpperCase();
          }
        }

        setText(hook('pubTitle'), s.title || '');
        var d = hook('pubDesc');  if (d) d.textContent = s.description || '';
        var c = hook('pubMeta');
        if (c) c.textContent = [s.category, s.area, s.city].filter(Boolean).join(' · ');

        var deals = hook('pubDeals');
        if (deals) deals.textContent = (s.deal_count || 0) + ' completed deals';

        var list = hook('pubListings');
        if (list) {
          list.innerHTML = items.length
            ? items.map(function (i) { return listingCard(i, { actions: false }); }).join('')
            : empty('Nothing listed yet');
        }

        var g = hook('pubGallery');
        if (g && gal.length) {
          g.innerHTML = gal.map(function (m) {
            return m.kind === 'video'
              ? '<video class="gp" src="' + esc(m.url) + '" controls playsinline></video>'
              : '<img class="gp" src="' + esc(m.url) + '" alt="" loading="lazy">';
          }).join('');
        }

        /* personal pages carry the activity block */
        if (kind === 'personal') {
          var a = hook('pubActivity');
          if (a && s.activity) {
            a.innerHTML =
              '<h2 class="sec-t">What I am up for</h2>' +
              '<p class="act-t">' + esc(s.activity) + '</p>' +
              '<dl class="act-d">' +
                (s.meeting_place ? '<dt>Where</dt><dd>' + esc(s.meeting_place) + '</dd>' : '') +
                (s.age ? '<dt>Age</dt><dd>' + esc(s.age) + '</dd>' : '') +
                (s.interested_in ? '<dt>Interested in</dt><dd>' + esc(s.interested_in) + '</dd>' : '') +
                (s.requirements ? '<dt>What you need</dt><dd>' + esc(s.requirements) + '</dd>' : '') +
              '</dl>';
          }
        }

        wireActions(s);
      }

      function wireActions(s) {
        var wrap = hook('pubActions');
        if (!wrap) return;

        wrap.addEventListener('click', function (e) {
          var b = e.target.closest('[data-act]');
          if (!b) return;
          if (!Session.token) return Session.requireLogin();

          var act = b.dataset.act;

          if (act === 'enquire' || act === 'order' || act === 'directions' || act === 'activity') {
            var ask = act === 'order' ? 'What would you like to order?'
                    : act === 'directions' ? 'Ask for directions'
                    : act === 'activity' ? 'Which activity, and when?'
                    : 'What would you like to ask?';
            return sheet({
              title: ask,
              note: 'This starts a conversation. Nothing is charged yet.',
              fields: [{ name: 'note', label: 'Your message',
                         placeholder: 'A few words is enough',
                         required: act !== 'directions' }],
              ok: 'Send'
            }).then(function (r) {
              if (!r) return;
              API.createRequest({ space_id: s.id, kind: act, note: r.values.note })
                .then(function () {
                  toast('Sent. Continue in Messages.');
                  setTimeout(function () { location.href = CFG.PAGES.messages; }, 800);
                })
                .catch(function (err) { toast(err.message); });
            });
          }

          if (act === 'follow') {
            var on = b.textContent.trim() !== 'Following';
            return API.follow(s.id, on).then(function () {
              b.textContent = on ? 'Following' : 'Follow';
            }).catch(function (err) { toast(err.message); });
          }

          if (act === 'visit') {
            return (location.href = CFG.PAGES.messages + '?space=' + encodeURIComponent(s.id));
          }

          if (act === 'report') {
            return API.report({ kind: 'space', target_id: s.id })
              .then(function () { toast('Reported. Thank you.'); })
              .catch(function (err) { toast(err.message); });
          }

          if (act === 'share') {
            var url = location.href;
            if (navigator.share) navigator.share({ title: s.title, url: url }).catch(quiet('a background request'));
            else if (navigator.clipboard) navigator.clipboard.writeText(url)
              .then(function () { toast('Link copied'); });
          }
        });
      }
    };
  }

  Screens['public-store']    = publicPage('store');
  Screens['public-services'] = publicPage('services');
  Screens['public-personal'] = publicPage('personal');

  /* -------------------------------------------------------------- MESSAGES
     STUB — LATER.
     The UI is real; the transport is not. When Firebase goes in, replace
     loadThreads/loadMessages/send with the realtime listeners and keep
     everything below unchanged.                                          */
  Screens.messages = function () {
    if (!Session.requireLogin()) return;

    var list = hook('threadList'), pane = hook('messagePane'),
        input = hook('msgInput'), sendBtn = hook('msgSend');
    var threadId = qs('deal') || qs('thread') || '';

    API.threads()
      .then(function (r) { paintThreads(r.threads || []); })
      .catch(function () {
        if (list) list.innerHTML = empty('Messages are switching on soon',
          'Live chat and video calling arrive in the next release. Your requests are safe in Requests.');
      });

    function paintThreads(threads) {
      if (!list) return;
      if (!threads.length) {
        list.innerHTML = empty('No conversations yet',
          'Start one from any store, service or personal page.');
        return;
      }
      list.innerHTML = threads.map(function (t) {
        return '<button class="thread" data-id="' + esc(t.id) + '">' +
          '<span class="thread-w">' + esc(t.title) + '</span>' +
          '<span class="thread-p">' + esc(t.preview || '') + '</span>' +
          '<span class="thread-t">' + ago(t.updated_at) + '</span></button>';
      }).join('');
      list.addEventListener('click', function (e) {
        var b = e.target.closest('.thread');
        if (b) { threadId = b.dataset.id; loadMessages(); }
      });
      if (threadId) loadMessages();
    }

    function loadMessages() {
      if (!pane || !threadId) return;
      API.messages(threadId).then(function (r) {
        pane.innerHTML = (r.messages || []).map(function (m) {
          return '<div class="bubble ' + (m.mine ? 'me' : 'them') + '">' +
            esc(m.text) + '<time>' + ago(m.created_at) + '</time></div>';
        }).join('');
        pane.scrollTop = pane.scrollHeight;
      }).catch(function () {
        pane.innerHTML = empty('Live messages arrive in the next release');
      });
    }

    if (sendBtn && input) sendBtn.addEventListener('click', function () {
      var text = input.value.trim();
      if (!text) return;
      API.send(threadId, text)
        .then(function () { input.value = ''; loadMessages(); })
        .catch(function () { toast('Live messages arrive in the next release'); });
    });

    var vid = hook('videoCall');
    if (vid) vid.addEventListener('click', function () {
      /* STUB — LATER: ₹49 / 30 min charge, then join the room. */
      toast('Video calling arrives in the next release');
    });
  };

  /* ======================================================================
     8. BOOT
     ====================================================================== */

  /* Paste `Comnito.diagnose()` into the browser console on any page that
     misbehaves. It answers the four questions that explain almost every
     "nothing happens" report. */
  function diagnose() {
    var root = document.querySelector('[data-screen]');
    var out = {
      screen: root ? root.dataset.screen : null,
      navTab: root ? root.dataset.page : null,
      controllerExists: root ? !!Screens[root.dataset.screen] : false,
      hooksFound: $$('[data-comnito]').length,
      hasToken: !!Session.token,
      apiBase: base(),
      theme: document.documentElement.getAttribute('data-comnito-theme'),
      cssLoaded: !!getComputedStyle(document.documentElement)
        .getPropertyValue('--navy').trim()
    };
    if (!root) {
      console.error('[Comnito] No element with data-screen. The page editor ' +
        'stripped it — re-paste the markup in <> HTML view.');
    } else if (!out.controllerExists) {
      console.error('[Comnito] data-screen="' + out.screen +
        '" has no controller. Valid: ' + Object.keys(Screens).join(', '));
    }
    if (!out.cssLoaded) {
      console.error('[Comnito] comnito.css did not load — check the asset name.');
    }
    console.table(out);
    fetch(base() + '/app/health')
      .then(function (r) { return r.json(); })
      .then(function (j) { console.log('[Comnito] Worker reachable:', j.version); })
      .catch(function (e) { console.error('[Comnito] Worker NOT reachable:', e.message); });
    return out;
  }

  /* Take the host theme's header off the app pages.
     There is a CSS rule for this too, so there is no flash on modern
     browsers — but it leans on :has(), which older Android browsers do not
     support and which fails silently. This is the belt. Only the fourteen
     Comnito pages are touched; the storefront keeps its header. */
  /* The three shared pages are somebody's shop, not Comnito's storefront.
     A navy Eurus footer with payment-card logos and "Designed by Omni
     Themes" under a tailor's page tells a visitor they are on a website
     builder, which is the opposite of the point. */
  var PUBLIC_SCREENS = ['public-store', 'public-services', 'public-personal'];

  function hideHostChrome(screen) {
    var sel = [
      '#shopify-section-header',
      '.header-wrapper',
      '.section-header',
      '[id^="shopify-section-"][id*="header"]',
      '[id^="shopify-section-"][id*="announcement"]'
    ];
    if (PUBLIC_SCREENS.indexOf(screen) > -1) {
      sel = sel.concat([
        '#shopify-section-footer',
        '.footer-wrapper',
        '[id^="shopify-section-"][id*="footer"]',
        'body > footer'
      ]);
    }
    $$(sel.join(',')).forEach(function (n) {
      if (n.querySelector('.comnito-app-wrapper')) return;  /* never ourselves */
      if (n.closest('.comnito-app')) return;                /* nor our own */
      n.style.display = 'none';
    });
  }

  function boot() {
    var root = document.querySelector('[data-screen]') || document.body;
    var screen = root.dataset.screen || '';

    if (screen) hideHostChrome(screen);

    watchTheme();
    mountChrome(root);

    var fn = Screens[screen];
    if (fn) {
      try { fn(root); }
      catch (e) {
        console.error('[Comnito] screen "' + screen + '" failed:', e);
        toast('Something went wrong on this screen');
      }
    } else if (screen) {
      console.warn('[Comnito] no controller for data-screen="' + screen + '"');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* public surface, handy from the console while you test */
  window.Comnito = {
    cfg: CFG, api: API, session: Session, toast: toast,
    screens: Screens, boot: boot, setNavBadge: setNavBadge,
    diagnose: diagnose, syncTheme: syncTheme, hook: hook
  };
  window.comnitoToast = toast;

})(window, document);
